import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';
import { PodApiClient } from '@theme/pod-api';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

const IMAGE_COMPRESSION_MAX_DIMENSION = 1800;
const IMAGE_COMPRESSION_MAX_BYTES = 1800 * 1024;
const IMAGE_COMPRESSION_QUALITY = 0.86;
const IMAGE_COMPRESSION_MIN_QUALITY = 0.68;
const PRODUCT_PAGE_SIZE = 21;

const DEFAULT_STYLE_PREVIEW_IMAGE =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><rect width="200" height="200" fill="#EEEAF8"/><g fill="none" stroke="#B5A6E3" stroke-width="7" stroke-linecap="round" stroke-linejoin="round"><rect x="40" y="48" width="120" height="104" rx="14"/><circle cx="78" cy="86" r="12"/><path d="M40 130l36-32 30 26 22-16 32 30"/></g></svg>'
  );

/**
 * Shopify /cart/add.js 不接受文件；合成图以 File 写入 properties[_tib_design_link_1]
 * （下划线开头，Checkout 不展示该字段；购物车主题用来当行缩略图）。
 */
function cartAddUrlForPreview(hasFile) {
  const url = String(Theme.routes?.cart_add_url || '/cart/add');
  return hasFile ? url.replace(/\.js$/, '') : url;
}

/**
 * @param {string} base64
 * @param {string} [mime]
 * @returns {File | null}
 */
function base64ToJpegFile(base64, mime = 'image/jpeg') {
  const cleaned = String(base64 || '').replace(/^data:[^;]+;base64,/, '');
  if (!cleaned) return null;
  const binary = atob(cleaned);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const type = String(mime || 'image/jpeg').trim() || 'image/jpeg';
  return new File([bytes], 'print-preview.jpg', { type });
}

/**
 * @typedef {{ id: string, previewUrl: string, style?: string, source?: string, petName?: string, matted?: boolean }} PodPattern
 * @typedef {object} CreateProductVariant
 * @property {number|string} id
 * @property {string} title
 * @property {boolean} available
 * @property {number} price
 * @property {string|null} option1
 * @property {string|null} option2
 * @property {string|null} option3
 * @property {string|null} featuredImage
 * @property {string} priceFormatted
 * @typedef {{ src: string, alt?: string, variantIds?: (number|string)[] }} CreateProductImage
 * @typedef {object} CreateProduct
 * @property {number|string} id
 * @property {string} handle
 * @property {string} title
 * @property {string} url
 * @property {string} [descriptionHtml]
 * @property {string|null} featuredImage
 * @property {string|null} [catalogImage]
 * @property {CreateProductImage[]} [images]
 * @property {string} priceMinFormatted
 * @property {string} [productType]
 * @property {string[]} [tags]
 * @property {string} [providerProductId]
 * @property {number} [providerType]
 * @property {string|null} [designImage]
  * @property {{ canvas?: { width: number, height: number }, bbox?: { left?: number, top?: number, width: number, height: number }, overlay?: { x: number, y: number, w: number, h: number, clip_path?: string, clipPath?: string }, overlays?: { x: number, y: number, w: number, h: number, clip_path?: string, clipPath?: string }[], mockup_url?: string, mockupUrl?: string, color?: string } | null} [printLayout]
 * @property {NonNullable<CreateProduct['printLayout']>[]} [printLayouts]
 * @property {boolean} [podPreviewChecked]
 * @property {{ name: string, values: string[] }[]} options
 * @property {CreateProductVariant[]} variants
 * @typedef {object} CreateStyleItem
 * @property {string} id
 * @property {string} category
 * @property {string} name
 * @property {string} previewUrl
 * @typedef {object} CreateStyleCategory
 * @property {string} id
 * @property {string} name
 */

/**
 * 4-step Create wizard: upload → style → portrait+products → product details → cart/buy.
 * @extends {Component}
 */
class CreatePortraitComponent extends Component {
  requiredRefs = [
    'selectedProductNotice',
    'selectedProductMain',
    'selectedProductImage',
    'selectedProductName',
    'changeProductButton',
    'progress',
    'stepsStage',
    'stepUpload',
    'stepStyle',
    'stepPortrait',
    'stepProduct',
    'fileInput',
    'petNameInput',
    'backgroundEffectList',
    'uploadZone',
    'uploadEmpty',
    'sourceImage',
    'uploadSuccess',
    'uploadOverlayUploading',
    'uploadRetryButton',
    'clearButton',
    'nextStyleButton',
    'styleFilters',
    'styleGrid',
    'generateButton',
    'portraitEmpty',
    'portraitResult',
    'portraitHint',
    'portraitImage',
    'portraitProgress',
    'favoriteButton',
    'patternList',
    'productList',
    'productListSentinel',
    'productLoadStatus',
    'nextProductButton',
    'detailImage',
    'detailDesignPreview',
    'detailDesignImage',
    'detailPetName',
    'purchaseTitle',
    'purchasePrice',
    'variantOptions',
    'detailGallery',
    'detailGalleryTitle',
    'detailGalleryNote',
    'detailDescription',
    'detailDescriptionBody',
    'quantityInput',
    'qtyMinus',
    'qtyPlus',
    'addToCartButton',
    'buyNowButton',
    'backToProductsButton',
    'statusMessage',
    'retryPollButton',
    'regenerateButton',
    'lightbox',
    'lightboxImage',
    'productsModal',
    'modalProductList',
    'modalProductListSentinel',
    'modalProductLoadStatus',
    'productImagesModal',
    'productImagesModalTitle',
    'productImagesModalList',
  ];

  /** @type {1|2|3|4} */
  #step = 1;
  /** @type {File | null} */
  #file = null;
  /** @type {File | null} */
  #originalFile = null;
  /** @type {string | null} */
  #objectUrl = null;
  /** @type {string | null} */
  #uploadId = null;
  /** @type {string} */
  #petName = '';
  /** @type {Promise<{ uploadId: string, sourceUrl?: string } | null> | null} */
  #uploadPromise = null;
  /** @type {boolean} */
  #uploading = false;
  /** @type {boolean} */
  #uploadFailed = false;
  /** @type {PodPattern[]} */
  #patterns = [];
  /** @type {PodPattern | null} */
  #confirmedDesign = null;
  /** @type {boolean} */
  #busy = false;
  /** @type {CreateProduct[]} */
  #products = [];
  /** @type {CreateProduct | null} */
  #selectedProduct = null;
  /** @type {string} */
  #preferredProductHandle = '';
  #preferredVariantId = '';
  /** Whether create page was opened with ?product= / ?template= */
  #enteredWithProductParam = false;
  /** @type {CreateProductVariant | null} */
  #selectedVariant = null;
  /** @type {Record<string, string>} */
  #optionSelections = {};
  /** @type {PodApiClient | null} */
  #api = null;
  /** @type {{ taskId: string, kind: 'patterns' | 'confirm' | 'matt', style?: string, petName?: string, backgroundEffect?: { mode: string, color?: string | null }, pattern?: PodPattern } | null} */
  #pendingPoll = null;
  /** @type {AbortController} */
  #lifecycle = new AbortController();
  /** @type {CreateStyleCategory[]} */
  #styleCategories = [];
  /** @type {CreateStyleItem[]} */
  #styles = [];
  /** @type {string} */
  #styleCategory = 'all';
  /** @type {string} */
  #selectedStyleId = '';
  /** @type {boolean} */
  #favorited = false;
  /** @type {boolean} */
  #matted = false;
  /** @type {{ mode: 'original' | 'transparent' | 'color', color: string | null }} */
  #backgroundEffect = { mode: 'transparent', color: null };
  #backgroundEffectBound = false;
  /** @type {Promise<void> | null} */
  #productPreviewsPromise = null;
  /** @type {boolean} */
  #productPreviewsLoading = false;
  /** @type {boolean} */
  #productPreviewsLoaded = false;
  /** @type {number} */
  #productPage = 0;
  /** @type {boolean} */
  #productLoading = false;
  /** @type {boolean} */
  #productHasMore = true;
  /** @type {IntersectionObserver | null} */
  #productLoadObserver = null;
  /** @type {IntersectionObserver | null} */
  #modalProductLoadObserver = null;
  /** @type {boolean} */
  #productsModalOpen = false;
  /** @type {ResizeObserver | null} */
  #printGuideObserver = null;
  /** @type {number} */
  #progressTimer = 0;
  /** @type {number} */
  #progressValue = 0;
  /** @type {number} */
  #progressTarget = 0;
  /** @type {string} */
  #progressMessage = '';

  get settings() {
    const configuredApiBaseUrl = (this.dataset.apiBaseUrl || '').replace(/\/$/, '');
    const configuredLocalApiBase = (this.dataset.localApiBaseUrl || '').replace(/\/$/, '');
    const localApiBaseUrl = configuredLocalApiBase || 'http://127.0.0.1:8001/shopify-pod';
    const useLocalApi = this.dataset.useLocalApi !== 'false';
    const isLocalHost = window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost';
    const apiBaseUrl = isLocalHost && useLocalApi ? localApiBaseUrl : configuredApiBaseUrl;

    return {
      apiBaseUrl,
      shopDomain: (this.dataset.shopDomain || '').trim().toLowerCase(),
      collectionHandle: (this.dataset.collectionHandle || 'all').trim() || 'all',
      demoMode: this.dataset.demoMode === 'true',
      moneyFormat: this.dataset.moneyFormat || '${{amount}}',
      currency: this.dataset.currency || 'USD',
      locale: (this.dataset.locale || document.documentElement.lang || 'en').trim() || 'en',
      stylePlaceholder: (this.dataset.stylePlaceholder || '').trim() || DEFAULT_STYLE_PREVIEW_IMAGE,
      strings: {
        uploading: this.dataset.i18nUploading || 'Uploading…',
        uploadSuccess: this.dataset.i18nUploadSuccess || 'Photo uploaded successfully!',
        generating: this.dataset.i18nGenerating || 'Generating your portrait…',
        loadingStyles: this.dataset.i18nLoadingStyles || 'Loading styles…',
        selectPattern: this.dataset.i18nSelectPattern || 'Select a portrait to continue',
        selectProduct: this.dataset.i18nSelectProduct || 'Select a product to continue',
        selectProductContinue: this.dataset.i18nSelectProductContinue || 'Product selected — tap Next to continue',
        chooseProduct: this.dataset.i18nChooseProduct || 'Choose a product',
        changeProduct: this.dataset.i18nChangeProduct || 'Change',
        confirmed: this.dataset.i18nConfirmed || 'Portrait ready — choose a product',
        errorGeneric: this.dataset.i18nError || 'Something went wrong. Please try again.',
        errorUpload: this.dataset.i18nErrorUpload || 'Could not upload this image.',
        uploadFailed: this.dataset.i18nUploadFailed || 'Upload failed',
        retryUpload: this.dataset.i18nRetryUpload || 'Tap to retry',
        addedToCart: this.dataset.i18nAddedToCart || 'Added to cart',
        adding: this.dataset.i18nAdding || 'Adding…',
        soldOut: this.dataset.i18nSoldOut || 'Sold out',
        retryPoll: this.dataset.i18nRetryPoll || 'Check task again',
        pollingTimeout:
          this.dataset.i18nPollingTimeout ||
          'Still generating — timed out waiting. You can check the task again.',
        checkingTask: this.dataset.i18nCheckingTask || 'Checking task…',
        unavailable: this.dataset.i18nUnavailable || 'This combination is unavailable',
        noProducts: this.dataset.i18nNoProducts || 'No products found.',
        noStyles: this.dataset.i18nNoStyles || 'No styles available.',
        shareTitle: this.dataset.i18nShareTitle || 'My AI Portrait',
        shareCopied: this.dataset.i18nShareCopied || 'Link copied',
        cutout: this.dataset.i18nCutout || 'Remove background',
        cutting: this.dataset.i18nCutting || 'Removing background…',
        cutoutDone: this.dataset.i18nCutoutDone || 'Background removed',
        backgroundColorDone:
          this.dataset.i18nBackgroundColorDone || 'Portrait ready with selected background',
        productImages: this.dataset.i18nProductImages || 'Product images',
        loadingProducts: this.dataset.i18nLoadingProducts || 'Loading products…',
        previewNeedDesign: this.dataset.i18nPreviewNeedDesign || 'Generate a portrait first',
        previewLoading: this.dataset.i18nPreviewLoading || 'Loading print preview…',
        previewNotMapped: this.dataset.i18nPreviewNotMapped || 'Product not mapped for print',
        previewNoLayout: this.dataset.i18nPreviewNoLayout || 'Missing print layout',
        previewNoMockup: this.dataset.i18nPreviewNoMockup || 'Missing product mockup',
        previewNoOverlay: this.dataset.i18nPreviewNoOverlay || 'Missing print area overlay',
        previewBadgeNotMapped: this.dataset.i18nPreviewBadgeNotMapped || 'Badge preview: product not mapped for print',
        previewBadgeNoLayout: this.dataset.i18nPreviewBadgeNoLayout || 'Badge preview: missing print layout',
        previewBadgeNoMockup: this.dataset.i18nPreviewBadgeNoMockup || 'Badge preview: missing product mockup',
        previewBadgeNoOverlay: this.dataset.i18nPreviewBadgeNoOverlay || 'Badge preview: missing print area config',
        previewBadgeInvalidOverlay:
          this.dataset.i18nPreviewBadgeInvalidOverlay || 'Badge preview: invalid print area size',
        previewBadgeFallback:
          this.dataset.i18nPreviewBadgeFallback || 'Badge preview: cannot compose full print overlay',
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.#products = [];
    this.#preferredProductHandle = this.templateHandleFromUrl();
    this.#preferredVariantId = new URLSearchParams(window.location.search).get('variant') || '';
    this.#enteredWithProductParam = Boolean(this.#preferredProductHandle);
    this.#petName = this.petName();
    this.#backgroundEffect = this.selectedBackgroundEffect();
    this.bindBackgroundEffectInputs();
    this.#api = this.createApi();
    this.bindFileInput();
    this.setupProductInfiniteScroll();
    this.setupModalProductInfiniteScroll();
    this.setStep(1, { scroll: false });
    this.hydrateFromHomepageCache();
    this.updatePreferredProductNotice();
    this.syncActions();
    this.loadStyles();
    this.bindPortraitPrintGuide();
    void this.ensureProductsLoaded();
    void this.ensurePreferredProduct().then(() => {
      this.updatePreferredProductNotice();
      this.#productPreviewsPromise = this.loadProductPreviews();
    });
  }

  productShopifyIds() {
    const ids = [];
    const seen = new Set();
    for (const product of this.#products) {
      const id = String(product.id || '').replace(/^gid:\/\/shopify\/Product\//i, '').trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
    return ids;
  }

  productDesignImage(product) {
    const mockup = product?.printLayout?.mockup_url || product?.printLayout?.mockupUrl || '';
    return mockup || product?.designImage || product?.featuredImage || '';
  }

  /**
   * @param {CreateProduct | null | undefined} product
   * @param {string} [portraitUrl]
   */
  isOverlayPreviewMode(product, portraitUrl = this.#confirmedDesign?.previewUrl || '') {
    if (!product || !portraitUrl) return false;
    return this.overlaysForLayout(this.printLayoutForProduct(product)).length > 0;
  }

  /**
   * @param {CreateProduct['printLayout'] | null | undefined} layout
   * @returns {'missing' | 'invalid_size' | null}
   */
  overlayIssueDetail(layout) {
    const overlays = this.overlaysForLayout(layout);
    if (!overlays.length) {
      const overlay = layout?.overlay;
      if (!overlay || typeof overlay !== 'object') return 'missing';
      return 'invalid_size';
    }
    return null;
  }

  /**
   * @param {CreateProduct | null | undefined} product
   * @param {string} [portraitUrl]
   */
  isBadgePreviewMode(product, portraitUrl = this.#confirmedDesign?.previewUrl || '') {
    if (!product || !portraitUrl) return false;
    return !this.isOverlayPreviewMode(product, portraitUrl);
  }

  /**
   * @param {CreateProduct | null | undefined} product
   */
  productShopifyId(product) {
    return String(product?.id || '')
      .replace(/^gid:\/\/shopify\/Product\//i, '')
      .trim();
  }

  /**
   * @param {string} message
   * @param {CreateProduct | null | undefined} product
   */
  formatPreviewIssue(message, product) {
    const shopifyId = this.productShopifyId(product);
    if (!shopifyId) return message;
    return `${message} · shopify_id: ${shopifyId}`;
  }

  /**
   * @param {CreateProduct | null | undefined} product
   * @returns {string | null}
   */
  resolvePrintPreviewIssueMessage(product) {
    if (!product || this.isOverlayPreviewMode(product)) return null;
    const { strings } = this.settings;
    const portraitUrl = this.#confirmedDesign?.previewUrl || '';
    // 步骤三商品列表：生成前/生成中不展示「请先生成」等遮罩提示
    if (!portraitUrl) return null;
    if (this.#busy || this.refs.portraitResult?.classList.contains('is-generating')) return null;
    const layout = this.printLayoutForProduct(product);
    const mockupUrl = layout?.mockup_url || layout?.mockupUrl || '';
    if (this.#productPreviewsLoading && !product.podPreviewChecked) return strings.previewLoading;
    if (this.isBadgePreviewMode(product, portraitUrl)) {
      if (product.podPreviewChecked && !product.providerProductId) return strings.previewBadgeNotMapped;
      if (!layout) return strings.previewBadgeNoLayout;
      if (!mockupUrl) return strings.previewBadgeNoMockup;
      const overlayIssue = this.overlayIssueDetail(layout);
      if (overlayIssue === 'missing') return strings.previewBadgeNoOverlay;
      if (overlayIssue === 'invalid_size') return strings.previewBadgeInvalidOverlay;
      return strings.previewBadgeFallback;
    }
    if (product.podPreviewChecked && !product.providerProductId) return strings.previewNotMapped;
    if (!layout) return strings.previewNoLayout;
    if (!mockupUrl) return strings.previewNoMockup;
    return strings.previewNoOverlay;
  }

  /**
   * @param {CreateProduct | null | undefined} product
   * @returns {string | null}
   */
  printPreviewIssue(product) {
    const message = this.resolvePrintPreviewIssueMessage(product);
    if (!message) return null;
    return this.formatPreviewIssue(message, product);
  }

  normalizeColorKey(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/（.*?）/g, '')
      .replace(/\(.*?\)/g, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  colorsMatch(left, right) {
    const a = this.normalizeColorKey(left);
    const b = this.normalizeColorKey(right);
    if (!a || !b) return false;
    return a === b || a.includes(b) || b.includes(a);
  }

  colorOptionName(product) {
    const options = product?.options || [];
    const named = options.find((option) => /color|colour|颜色|色彩/i.test(String(option.name || '')));
    if (named) return named.name;
    const layouts = product?.printLayouts || [];
    let bestName = '';
    let bestHits = 0;
    options.forEach((option) => {
      const hits = (option.values || []).filter((value) =>
        layouts.some((layout) => this.colorsMatch(value, layout?.color)),
      ).length;
      if (hits > bestHits) {
        bestHits = hits;
        bestName = option.name;
      }
    });
    return bestName;
  }

  selectedColorValue(product) {
    const name = this.colorOptionName(product);
    if (!name) return '';
    return this.#optionSelections[name] || '';
  }

  printLayoutForProduct(product, colorValue = '') {
    const layouts = Array.isArray(product?.printLayouts) ? product.printLayouts : [];
    const fallback = product?.printLayout || layouts[0] || null;
    const color = colorValue || this.selectedColorValue(product);
    if (!color || !layouts.length) return fallback;
    const exact = layouts.find((layout) => this.normalizeColorKey(layout?.color) === this.normalizeColorKey(color));
    if (exact) return exact;
    return layouts.find((layout) => this.colorsMatch(color, layout?.color)) || fallback;
  }

  /**
   * @param {{ x?: number, y?: number, w?: number, h?: number }} item
   */
  overlayVisibleArea(item) {
    const x = Number(item?.x || 0);
    const y = Number(item?.y || 0);
    const w = Number(item?.w || 0);
    const h = Number(item?.h || 0);
    const x0 = Math.max(0, x);
    const x1 = Math.min(1, x + w);
    const y0 = Math.max(0, y);
    const y1 = Math.min(1, y + h);
    return Math.max(0, x1 - x0) * Math.max(0, y1 - y0);
  }

  /**
   * 侧边 wrap：画布内只剩细缝；双杯第二面通常仍是块状可见区。
   * @param {{ x?: number, y?: number, w?: number, h?: number }} item
   */
  isThinVisibleStrip(item) {
    const x = Number(item?.x || 0);
    const y = Number(item?.y || 0);
    const w = Number(item?.w || 0);
    const h = Number(item?.h || 0);
    const vx = Math.max(0, Math.min(1, x + w) - Math.max(0, x));
    const vy = Math.max(0, Math.min(1, y + h) - Math.max(0, y));
    if (vx <= 1e-9 || vy <= 1e-9) return true;
    // 画框侧边常见可见宽约 0.2–0.25，略放宽细缝判定
    if (Math.min(vx, vy) < 0.12) return true;
    return Math.min(vx, vy) / Math.max(vx, vy) < 0.28;
  }

  /**
   * @param {{ x?: number, y?: number, w?: number, h?: number }} a
   * @param {{ x?: number, y?: number, w?: number, h?: number }} b
   */
  overlayIoU(a, b) {
    const ax0 = Number(a?.x || 0);
    const ay0 = Number(a?.y || 0);
    const aw = Number(a?.w || 0);
    const ah = Number(a?.h || 0);
    const bx0 = Number(b?.x || 0);
    const by0 = Number(b?.y || 0);
    const bw = Number(b?.w || 0);
    const bh = Number(b?.h || 0);
    const ax1 = ax0 + aw;
    const ay1 = ay0 + ah;
    const bx1 = bx0 + bw;
    const by1 = by0 + bh;
    const ix0 = Math.max(ax0, bx0);
    const iy0 = Math.max(ay0, by0);
    const ix1 = Math.min(ax1, bx1);
    const iy1 = Math.min(ay1, by1);
    const inter = Math.max(0, ix1 - ix0) * Math.max(0, iy1 - iy0);
    const union = aw * ah + bw * bh - inter;
    if (union <= 1e-12) return 0;
    return inter / union;
  }

  /**
   * 是否带 PlLd 智能对象四角。
   * @param {{ quad?: unknown }} overlay
   */
  hasSmartQuad(overlay) {
    const quad = overlay?.quad;
    return Array.isArray(quad) && quad.length >= 4;
  }

  /**
   * @param {CreateProduct['printLayout'] | null | undefined} layout
   * @returns {{ x: number, y: number, w: number, h: number, clip_path?: string, clipPath?: string }[]}
   */
  primaryOverlayForLayout(layout) {
    const overlays = this.pasteOverlaysForLayout(layout);
    if (!overlays.length) return null;
    return overlays.reduce(
      (best, item) => (this.overlayVisibleArea(item) > this.overlayVisibleArea(best) ? item : best),
      overlays[0],
    );
  }

  /**
   * 细缝 clip_path（错绑侧边蒙版）会导致正面被裁没，改用 quad。
   * 仅当画布内可见区本身是细缝（真·侧边 wrap）时保留细缝 clip。
   * @param {{ x?: number, y?: number, w?: number, h?: number, quad?: { x: number, y: number }[], clip_path?: string, clipPath?: string }} overlay
   */
  sanitizeOverlayClip(overlay) {
    if (!overlay) return overlay;
    const clip = overlay.clip_path || overlay.clipPath || '';
    const pairs = [...String(clip).matchAll(/(-?[\d.]+)\s*%\s+(-?[\d.]+)\s*%/g)].map((m) => [
      Number(m[1]),
      Number(m[2]),
    ]);
    if (pairs.length >= 3) {
      const xs = pairs.map((p) => p[0]);
      const ys = pairs.map((p) => p[1]);
      const spanX = Math.max(...xs) - Math.min(...xs);
      const spanY = Math.max(...ys) - Math.min(...ys);
      if (spanX >= 8 && spanY >= 8) return overlay;
    } else if (!clip) {
      return overlay;
    }
    const x = Number(overlay.x || 0);
    const y = Number(overlay.y || 0);
    const w = Number(overlay.w || 0);
    const h = Number(overlay.h || 0);
    const total = Math.max(w * h, 1e-9);
    const visible = this.overlayVisibleArea(overlay);
    // 真·侧边 wrap：可见比例低且可见区呈细缝
    if (visible / total < 0.7 && this.isThinVisibleStrip(overlay)) return overlay;

    const quad = Array.isArray(overlay.quad) ? overlay.quad : [];
    const ow = Math.max(w, 0.001);
    const oh = Math.max(h, 0.001);
    let rebuilt = '';
    if (quad.length >= 3) {
      const parts = quad
        .map((point) => {
          const lx = ((Number(point?.x || 0) - x) / ow) * 100;
          const ly = ((Number(point?.y || 0) - y) / oh) * 100;
          return `${lx.toFixed(2)}% ${ly.toFixed(2)}%`;
        })
        .filter(Boolean);
      if (parts.length >= 3) rebuilt = `polygon(${parts.join(', ')})`;
    }
    return { ...overlay, clip_path: rebuilt, clipPath: rebuilt };
  }

  /**
   * @param {CreateProduct['printLayout'] | null | undefined} layout
   */
  overlaysForLayout(layout) {
    if (!layout) return [];
    const fromList = Array.isArray(layout.overlays) ? layout.overlays : [];
    const valid = fromList.filter((item) => item && Number(item.w) > 0 && Number(item.h) > 0);
    const source = valid.length
      ? valid
      : layout.overlay && Number(layout.overlay.w) > 0 && Number(layout.overlay.h) > 0
        ? [layout.overlay]
        : [];
    return source.map((item) => this.sanitizeOverlayClip(item));
  }

  /**
   * PSD 素材层名；空名回落为「素材」。
   * @param {{ name?: string }} overlay
   */
  materialLayerName(overlay) {
    const name = String(overlay?.name || '').trim();
    return name || '素材';
  }

  /**
   * 单肖像主素材槽位：优先「素材」，否则可见面积合计最大的层名。
   * @param {{ name?: string, x?: number, y?: number, w?: number, h?: number }[]} overlays
   */
  pickPrimaryMaterialName(overlays) {
    /** @type {Record<string, number>} */
    const scores = {};
    (overlays || []).forEach((item) => {
      const name = this.materialLayerName(item);
      scores[name] = (scores[name] || 0) + this.overlayVisibleArea(item);
    });
    const keys = Object.keys(scores);
    if (!keys.length) return '素材';
    if ((scores['素材'] || 0) > 0) return '素材';
    return keys.reduce((best, name) => (scores[name] > scores[best] ? name : best), keys[0]);
  }

  /**
   * 绘制顺序：PSD 自下而上（layer_index 升序）。
   * 无 layer_index 时：画布中心 y 升序（上方先画，下方后画盖住）。
   * @param {{ layer_index?: number, layerIndex?: number, y?: number, h?: number, name?: string }} a
   * @param {{ layer_index?: number, layerIndex?: number, y?: number, h?: number, name?: string }} b
   */
  overlayPaintOrder(a, b) {
    const rawA = a?.layer_index ?? a?.layerIndex;
    const rawB = b?.layer_index ?? b?.layerIndex;
    const ai = Number(rawA);
    const bi = Number(rawB);
    const hasA = Number.isFinite(ai);
    const hasB = Number.isFinite(bi);
    if (hasA && hasB && ai !== bi) return ai - bi;
    if (hasA !== hasB) return hasA ? -1 : 1;
    const ay = Number(a?.y || 0) + Number(a?.h || 0) * 0.5;
    const by = Number(b?.y || 0) + Number(b?.h || 0) * 0.5;
    if (ay !== by) return ay - by;
    return this.materialLayerName(a).localeCompare(this.materialLayerName(b));
  }

  /**
   * 贴图目标：全部素材区域可贴同一张图，但每层只用自己的坐标/quad/蒙版（禁止串用）。
   * - 同名多实例：同图 + 各层蒙版区域
   * - 异名（素材 / 素材2）：也可贴同一肖像，几何各自独立
   * - 同名近重合 / 画布外镜像 wrap 丢弃
   * - 返回顺序自下而上，DOM 后挂的盖在上面
   * @param {CreateProduct['printLayout'] | null | undefined} layout
   */
  pasteOverlaysForLayout(layout) {
    const all = this.overlaysForLayout(layout);
    if (!all.length) return [];

    /** @type {Record<string, typeof all>} */
    const groups = {};
    all.forEach((item) => {
      const name = this.materialLayerName(item);
      if (!groups[name]) groups[name] = [];
      groups[name].push(item);
    });

    /** @type {typeof all} */
    const keptAll = [];
    Object.keys(groups).forEach((name) => {
      const group = groups[name];
      const withQuad = group.filter((item) => this.hasSmartQuad(item));
      const pool = (withQuad.length ? withQuad : group)
        .slice()
        .sort((a, b) => this.overlayVisibleArea(b) - this.overlayVisibleArea(a));
      /** @type {typeof all} */
      const kept = [];
      pool.forEach((item) => {
        if (kept.some((prev) => this.overlayIoU(item, prev) >= 0.45)) return;
        kept.push(item);
      });
      if (kept.length <= 1) {
        keptAll.push(...kept);
        return;
      }
      const primary = kept[0];
      const pw = Number(primary.w || 0);
      const ph = Number(primary.h || 0);
      kept.forEach((item, index) => {
        if (index === 0) {
          keptAll.push(item);
          return;
        }
        const area = Math.max(Number(item.w || 0) * Number(item.h || 0), 1e-9);
        const visibleRatio = this.overlayVisibleArea(item) / area;
        const sameSize =
          Math.abs(Number(item.w || 0) - pw) < 0.05 && Math.abs(Number(item.h || 0) - ph) < 0.05;
        const x = Number(item.x || 0);
        const y = Number(item.y || 0);
        const w = Number(item.w || 0);
        const h = Number(item.h || 0);
        // 画布外镜像 wrap（如画框侧面）：同尺寸、框伸出画布、可见面积明显更小
        const extendsOutside = x < -0.02 || y < -0.02 || x + w > 1.02 || y + h > 1.02;
        const muchLessVisible =
          this.overlayVisibleArea(item) < this.overlayVisibleArea(primary) * 0.75;
        // 仅丢掉侧边细缝 / 画布外镜像 wrap；保留双杯/多枕第二块状面
        if (
          sameSize &&
          ((visibleRatio < 0.7 && this.isThinVisibleStrip(item)) ||
            (extendsOutside && visibleRatio < 0.65 && muchLessVisible))
        ) {
          return;
        }
        keptAll.push(item);
      });
    });

    return keptAll.sort((a, b) => this.overlayPaintOrder(a, b));
  }

  /**
   * @param {CreateProduct['printLayout']} layout
   * @returns {{ width: number, height: number } | null}
   */
  printAreaFromLayout(layout) {
    if (!layout) return null;
    const bbox = layout.bbox || {};
    const overlay = this.primaryOverlayForLayout(layout) || layout.overlay || {};
    const canvas = layout.canvas || {};
    const bboxW = Number(bbox.width || 0);
    const bboxH = Number(bbox.height || 0);
    const overlayW = Number(overlay.w || 0);
    const overlayH = Number(overlay.h || 0);
    const canvasW = Number(canvas.width || 0);
    const canvasH = Number(canvas.height || 0);
    let width = 0;
    let height = 0;
    if (bboxW > 0 && bboxH > 0) {
      width = bboxW;
      height = bboxH;
    } else if (overlayW > 0 && overlayH > 0 && canvasW > 0 && canvasH > 0) {
      width = overlayW * canvasW;
      height = overlayH * canvasH;
    } else if (overlayW > 0 && overlayH > 0) {
      width = overlayW;
      height = overlayH;
    }
    if (!(width > 0 && height > 0)) return null;
    return { width, height };
  }

  /**
   * @param {CreateProduct | null} [product]
   * @returns {{ width: number, height: number } | null}
   */
  printAreaForProduct(product = null) {
    if (product) return this.printAreaFromLayout(this.printLayoutForProduct(product));
    if (this.#selectedProduct) {
      const selected = this.printAreaFromLayout(this.printLayoutForProduct(this.#selectedProduct));
      if (selected) return selected;
    }
    for (const item of this.#products) {
      const area = this.printAreaFromLayout(this.printLayoutForProduct(item));
      if (area) return area;
    }
    return null;
  }

  bindPortraitPrintGuide() {
    const image = this.refs.portraitImage;
    const frame = image?.closest('.create-portrait__portrait-frame');
    image?.addEventListener('load', () => this.updatePortraitPrintGuide());
    if (typeof ResizeObserver === 'function' && frame instanceof HTMLElement) {
      this.#printGuideObserver = new ResizeObserver(() => this.updatePortraitPrintGuide());
      this.#printGuideObserver.observe(frame);
    }
    this.updatePortraitPrintGuide();
  }

  clearPortraitPrintLayout() {
    const image = this.refs.portraitImage;
    if (!(image instanceof HTMLImageElement)) return;
    image.style.removeProperty('left');
    image.style.removeProperty('top');
    image.style.removeProperty('width');
    image.style.removeProperty('height');
    image.style.removeProperty('inset');
  }

  updatePortraitPrintGuide() {
    const image = this.refs.portraitImage;
    const frame = image?.closest('.create-portrait__portrait-frame');
    if (!(image instanceof HTMLImageElement) || !(frame instanceof HTMLElement)) return;
    const generating = this.refs.portraitResult?.classList.contains('is-generating');
    const area = this.printAreaForProduct();
    const ready = Boolean(image.currentSrc && image.naturalWidth > 0);
    if (generating || !area || !ready) {
      this.clearPortraitPrintLayout();
      return;
    }
    const frameW = frame.clientWidth;
    const frameH = frame.clientHeight;
    if (!(frameW > 0) || !(frameH > 0)) return;
    const shown = containRect(frameW, frameH, area.width, area.height);
    if (!(shown.w > 2) || !(shown.h > 2)) {
      this.clearPortraitPrintLayout();
      return;
    }
    image.style.inset = 'auto';
    image.style.left = `${shown.x}px`;
    image.style.top = `${shown.y}px`;
    image.style.width = `${shown.w}px`;
    image.style.height = `${shown.h}px`;
  }

  async loadProductPreviews(productsToFetch = null) {
    const source = productsToFetch || this.#products;
    if (!this.#api || !source.length) return;
    const shopifyIds = source
      .map((product) => String(product.id || '').replace(/^gid:\/\/shopify\/Product\//i, '').trim())
      .filter(Boolean);
    if (!shopifyIds.length) return;
    const fetchedIds = new Set(shopifyIds);
    const refreshedProducts = () =>
      this.#products.filter((product) => {
        const productId = String(product.id || '')
          .replace(/^gid:\/\/shopify\/Product\//i, '')
          .trim();
        return fetchedIds.has(productId);
      });
    this.#productPreviewsLoading = true;
    this.updateProductPreviewIssues(refreshedProducts());
    console.info('[create-portrait] shopifyIds', shopifyIds);
    try {
      const data = await this.#api.listMappedProducts(shopifyIds);
      console.info('[create-portrait] listMappedProducts data', data);
      const byId = new Map((data.products || []).map((item) => [String(item.shopifyId), item]));
      /** @type {CreateProduct[]} */
      const changedProducts = [];
      this.#products = this.#products.map((product) => {
        const productId = String(product.id || '').replace(/^gid:\/\/shopify\/Product\//i, '').trim();
        if (!fetchedIds.has(productId)) return product;
        const mapped = byId.get(productId) || byId.get(String(product.handle || '').trim());
        if (!mapped?.imgUrl && !mapped?.providerProductId) {
          const checkedOnly = { ...product, podPreviewChecked: true };
          changedProducts.push(checkedOnly);
          return checkedOnly;
        }
        const printLayout = mapped.printLayout || product.printLayout || null;
        const printLayouts = Array.isArray(mapped.printLayouts)
          ? mapped.printLayouts
          : product.printLayouts || [];
        const designImage =
          printLayout?.mockup_url ||
          printLayout?.mockupUrl ||
          mapped.imgUrl ||
          product.designImage ||
          product.featuredImage ||
          null;
        const updated = {
          ...product,
          designImage,
          // Preserve Shopify catalog image (homepage card). Never replace with POD mockup.
          featuredImage: product.featuredImage || product.catalogImage || null,
          catalogImage: product.catalogImage || product.featuredImage || null,
          providerProductId: mapped.providerProductId || product.providerProductId,
          providerType: mapped.providerType || product.providerType,
          printLayout,
          printLayouts,
          podPreviewChecked: true,
        };
        changedProducts.push(updated);
        return updated;
      });
      this.#productPreviewsLoaded = true;
      console.info('[create-portrait] listMappedProducts changed', changedProducts.length);
      if (this.#selectedProduct) {
        const updated = this.#products.find((item) => String(item.id) === String(this.#selectedProduct.id));
        if (updated) this.#selectedProduct = updated;
      }
      this.updatePreferredProductNotice();
      // Must use refreshed #products (with printLayout), not the stale `source` objects.
      this.updateProductCards(refreshedProducts());
      if (this.#step === 4 && this.#selectedProduct) this.renderProductDetail();
      this.updatePortraitPrintGuide();
    } catch (error) {
      console.info('[create-portrait] mapped products failed', error);
    } finally {
      this.#productPreviewsLoading = false;
      this.updateProductPreviewIssues(refreshedProducts());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#productLoadObserver?.disconnect();
    this.#productLoadObserver = null;
    this.#modalProductLoadObserver?.disconnect();
    this.#modalProductLoadObserver = null;
    this.#printGuideObserver?.disconnect();
    this.#printGuideObserver = null;
    this.#lifecycle.abort();
    this.#lifecycle = new AbortController();
    this.#api?.abort();
    this.revokeObjectUrl();
  }

  createApi() {
    const { apiBaseUrl, shopDomain, demoMode, strings } = this.settings;
    return new PodApiClient({
      apiBaseUrl,
      shopDomain,
      demoMode,
      genericError: () => strings.errorGeneric,
      uploadError: () => strings.errorUpload,
      pollingTimeout: () => strings.pollingTimeout,
      debug: (message, detail) => {
        if (this.dataset.debug === 'true') console.info('[create-portrait]', message, detail ?? '');
      },
    });
  }

  templateHandleFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      return String(params.get('template') || params.get('product') || '').trim();
    } catch {
      return '';
    }
  }

  /**
   * Instantly hydrate Selected product from homepage click cache to avoid notice pop-in.
   */
  hydrateFromHomepageCache() {
    const handle = this.#preferredProductHandle;
    if (!handle) return;
    /** @type {{ handle?: string, title?: string, image?: string } | null} */
    let cached = null;
    try {
      const raw = sessionStorage.getItem('pettaa-preferred-product');
      cached = raw ? JSON.parse(raw) : null;
    } catch {
      cached = null;
    }
    const cachedHandle = String(cached?.handle || '').trim().toLowerCase();
    if (cachedHandle && cachedHandle === handle.toLowerCase()) {
      const image = this.absoluteImageUrl(cached?.image || '');
      const stub = {
        id: `pending:${handle}`,
        handle,
        title: String(cached?.title || handle),
        url: `/products/${handle}`,
        featuredImage: image,
        catalogImage: image,
        images: image ? [{ src: image, variantIds: [] }] : [],
        priceMinFormatted: '',
        options: [],
        variants: [],
      };
      if (!this.preferredProduct()) {
        this.#products = [stub, ...this.#products];
      }
      if (!this.#selectedProduct) this.#selectedProduct = stub;
      return;
    }
    // No cache: still reserve the notice with handle so layout doesn't jump later.
    if (!this.#selectedProduct) {
      this.#selectedProduct = {
        id: `pending:${handle}`,
        handle,
        title: handle,
        url: `/products/${handle}`,
        featuredImage: null,
        catalogImage: null,
        images: [],
        priceMinFormatted: '',
        options: [],
        variants: [],
      };
    }
  }

  preferredProduct() {
    const handle = this.#preferredProductHandle.toLowerCase();
    if (!handle) return null;
    return this.#products.find((product) => String(product.handle || '').toLowerCase() === handle) || null;
  }

  promotePreferredProduct() {
    const preferred = this.preferredProduct();
    if (!preferred) return;
    this.#products = [preferred, ...this.#products.filter((item) => String(item.id) !== String(preferred.id))];
  }

  /**
   * @param {{ requireDesign?: boolean, silent?: boolean }} [options]
   */
  applyPreferredProduct(options = {}) {
    const preferred = this.preferredProduct();
    if (!preferred) {
      // No URL/session preferred product: keep a default selection so the top bar stays filled.
      if (!this.#selectedProduct) {
        const first = this.catalogProducts()[0] || this.#products.find((item) => !String(item.id).startsWith('pending:'));
        if (first) {
          this.selectProduct(first, {
            requireDesign: options.requireDesign === true,
            silent: options.silent !== false,
          });
          return;
        }
      }
      this.updatePreferredProductNotice();
      return;
    }
    const selectedHandle = String(this.#selectedProduct?.handle || '').toLowerCase();
    const preferredHandle = String(preferred.handle || '').toLowerCase();
    if (
      this.#selectedProduct &&
      selectedHandle &&
      selectedHandle !== preferredHandle &&
      String(this.#selectedProduct.id) !== String(preferred.id)
    ) {
      this.updatePreferredProductNotice();
      return;
    }
    this.selectProduct(preferred, {
      requireDesign: options.requireDesign === true,
      silent: options.silent !== false,
    });
  }

  updatePreferredProductNotice() {
    const notice = this.refs.selectedProductNotice;
    const name = this.refs.selectedProductName;
    const image = this.refs.selectedProductImage;
    if (!(notice instanceof HTMLElement) || !(name instanceof HTMLElement)) return;
    const product = this.#selectedProduct || this.preferredProduct();
    // With URL product params: show from entry. Without params: only on step 4.
    const shouldShow = this.#enteredWithProductParam || this.#step === 4;
    notice.hidden = !shouldShow;
    if (!shouldShow) {
      name.textContent = '';
      if (image instanceof HTMLImageElement) {
        image.hidden = true;
        image.removeAttribute('src');
        image.alt = '';
      }
      return;
    }
    if (product) {
      name.textContent = product.title || this.#preferredProductHandle || this.settings.strings.chooseProduct;
      const imageUrl = this.productCatalogImage(product) || '';
      if (image instanceof HTMLImageElement) {
        if (imageUrl) {
          image.hidden = false;
          image.src = imageUrl;
          image.alt = product.title || this.#preferredProductHandle || '';
        } else {
          image.hidden = true;
          image.removeAttribute('src');
          image.alt = '';
        }
      }
      return;
    }
    name.textContent = this.settings.strings.chooseProduct;
    if (image instanceof HTMLImageElement) {
      image.hidden = true;
      image.removeAttribute('src');
      image.alt = '';
    }
  }

  async ensurePreferredProduct() {
    const handle = this.#preferredProductHandle;
    if (!handle) return;
    if (this.preferredProduct() && !String(this.preferredProduct()?.id || '').startsWith('pending:')) {
      this.promotePreferredProduct();
      this.refreshProductLists();
      return;
    }
    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
      if (!response.ok) return;
      const data = await response.json();
      const product = this.normalizeShopifyProduct(data);
      if (!product) return;
      this.#products = [product, ...this.#products.filter((item) => String(item.handle || '').toLowerCase() !== handle.toLowerCase())];
      if (
        !this.#selectedProduct ||
        String(this.#selectedProduct.handle || '').toLowerCase() === handle.toLowerCase() ||
        String(this.#selectedProduct.id || '').startsWith('pending:')
      ) {
        this.#selectedProduct = product;
      }
      this.refreshProductLists();
      this.updatePreferredProductNotice();
    } catch {
      /* Featured card product may not be in the create collection. */
    }
  }

  /**
   * @param {Record<string, unknown>} data
   * @returns {CreateProduct | null}
   */
  normalizeShopifyProduct(data) {
    if (!data || typeof data !== 'object') return null;
    const handle = String(data.handle || '').trim();
    if (!handle) return null;
    const firstImage =
      Array.isArray(data.images) && data.images[0] && typeof data.images[0] === 'object'
        ? data.images[0].src
        : Array.isArray(data.images) && typeof data.images[0] === 'string'
          ? data.images[0]
          : '';
    // Match homepage: Liquid uses featured_image; infinite-scroll cards fall back to images[0].
    const featuredImage =
      this.absoluteImageUrl(data.featured_image || data.featuredImage) ||
      this.absoluteImageUrl(firstImage);
    const options = (Array.isArray(data.options) ? data.options : []).map((option) => ({
      name: String(option?.name || ''),
      values: Array.isArray(option?.values) ? option.values.map(String) : [],
    }));
    /** @type {Map<string, number[]>} */
    const imageVariantMap = new Map();
    (Array.isArray(data.variants) ? data.variants : []).forEach((variant) => {
      const featured = variant?.featured_image;
      const src = this.absoluteImageUrl(featured?.src || variant?.featuredImage || '');
      if (!src) return;
      const variantIds = Array.isArray(featured?.variant_ids)
        ? featured.variant_ids.map(Number)
        : [Number(variant?.id || 0)].filter(Boolean);
      const existing = imageVariantMap.get(src) || [];
      imageVariantMap.set(src, [...new Set([...existing, ...variantIds])]);
    });
    /** @type {CreateProductImage[]} */
    const images = [];
    const seenImages = new Set();
    const addImage = (src, variantIds = []) => {
      const url = this.absoluteImageUrl(src);
      if (!url || seenImages.has(url)) return;
      seenImages.add(url);
      images.push({
        src: url,
        variantIds: [...new Set([...(imageVariantMap.get(url) || []), ...variantIds.map(Number)])].filter(Boolean),
      });
    };
    (Array.isArray(data.images) ? data.images : []).forEach((image) => {
      if (typeof image === 'string') addImage(image);
      else if (image && typeof image === 'object') {
        addImage(image.src, image.variant_ids || image.variantIds || []);
      }
    });
    imageVariantMap.forEach((variantIds, src) => addImage(src, variantIds));
    const variants = (Array.isArray(data.variants) ? data.variants : []).map((variant) => {
      const price = this.variantPriceToCents(variant?.price);
      const variantImage = variant?.featured_image?.src || variant?.featuredImage || null;
      return {
        id: variant?.id,
        title: String(variant?.title || ''),
        available: Boolean(variant?.available),
        price,
        priceFormatted: this.formatCents(price),
        option1: variant?.option1 ?? null,
        option2: variant?.option2 ?? null,
        option3: variant?.option3 ?? null,
        featuredImage: this.absoluteImageUrl(variantImage),
      };
    });
    const tags = Array.isArray(data.tags)
      ? data.tags.map(String)
      : String(data.tags || '')
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean);
    const variantPrices = variants.map((variant) => Number(variant.price || 0)).filter((price) => price > 0);
    const priceMin =
      this.variantPriceToCents(data.price_min ?? data.price) ||
      (variantPrices.length ? Math.min(...variantPrices) : 0);
    return {
      id: data.id,
      handle,
      title: String(data.title || ''),
      url: String(data.url || `/products/${handle}`),
      descriptionHtml: String(data.description || data.body_html || data.bodyHtml || ''),
      featuredImage,
      catalogImage: featuredImage,
      images,
      priceMinFormatted: this.formatCents(priceMin),
      productType: String(data.type || data.product_type || ''),
      tags,
      options,
      variants,
    };
  }

  absoluteImageUrl(value) {
    if (value && typeof value === 'object') {
      value = value.src || value.url || '';
    }
    const url = String(value || '').trim();
    if (!url || url === '[object Object]') return null;
    if (url.startsWith('//')) return `https:${url}`;
    return url;
  }

  /**
   * Catalog image shown on the homepage product cards (Shopify featured / first gallery image).
   * Never use POD mockup / designImage here.
   * @param {CreateProduct | null | undefined} product
   */
  productCatalogImage(product) {
    if (!product) return '';
    return (
      this.absoluteImageUrl(product.catalogImage) ||
      this.absoluteImageUrl(product.featuredImage) ||
      this.absoluteImageUrl(product.images?.[0]?.src) ||
      ''
    );
  }

  /**
   * Shopify collection products.json returns decimal strings; product.js returns integer cents.
   * @param {unknown} price
   */
  variantPriceToCents(price) {
    if (price == null || price === '') return 0;
    if (typeof price === 'string') {
      const minor = convertMoneyToMinorUnits(price, this.settings.currency);
      return minor == null ? 0 : minor;
    }
    return Math.round(Number(price) || 0);
  }

  formatCents(cents) {
    return this.formatPrice(cents);
  }

  bindFileInput() {
    const input = this.refs.fileInput;
    if (!(input instanceof HTMLInputElement) || input.dataset.bound === 'true') return;
    input.dataset.bound = 'true';
    input.addEventListener('change', this.handleFileChange, { signal: this.#lifecycle.signal });
  }

  /**
   * @param {1|2|3|4} step
   * @param {{ scroll?: boolean }} [options]
   */
  setStep(step, options = {}) {
    const { scroll = true } = options;
    const previous = this.#step;
    this.#step = step;
    this.dataset.step = String(step);
    const map = {
      1: this.refs.stepUpload,
      2: this.refs.stepStyle,
      3: this.refs.stepPortrait,
      4: this.refs.stepProduct,
    };
    Object.entries(map).forEach(([key, el]) => {
      if (!(el instanceof HTMLElement)) return;
      const n = Number(key);
      el.hidden = n !== step;
      el.classList.toggle('is-current', n === step);
      el.classList.toggle('is-complete', n < step);
      el.classList.toggle('is-disabled', false);
    });

    this.refs.progress?.querySelectorAll('[data-step]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const n = Number(el.dataset.step);
      el.classList.toggle('is-active', n === step);
      el.classList.toggle('is-complete', n < step);
    });

    this.updatePreferredProductNotice();

    if (scroll && step !== previous) {
      this.scrollToCurrentStep();
    }
    if (step === 3) void this.ensureProductsLoaded();
  }

  scrollToCurrentStep() {
    const map = {
      1: this.refs.stepUpload,
      2: this.refs.stepStyle,
      3: this.refs.stepPortrait,
      4: this.refs.stepProduct,
    };
    const target = map[this.#step];
    if (!(target instanceof HTMLElement)) return;

    requestAnimationFrame(() => {
      const root = this.getScrollRoot();
      const offset = 16;
      if (root === document.scrollingElement || root === document.documentElement || root === document.body) {
        const top = window.scrollY + target.getBoundingClientRect().top - offset;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        return;
      }
      if (root instanceof HTMLElement) {
        const rootRect = root.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        root.scrollTo({
          top: Math.max(0, root.scrollTop + targetRect.top - rootRect.top - offset),
          behavior: 'smooth',
        });
      }
    });
  }

  /**
   * @param {string} message
   * @param {'error'|'success'|''} [tone]
   * @param {{ showRetry?: boolean }} [options]
   */
  setStatus(message, tone = '', options = {}) {
    const el = this.refs.statusMessage;
    el.textContent = message;
    el.classList.toggle('is-error', tone === 'error');
    el.classList.toggle('is-success', tone === 'success');
    const retry = this.refs.retryPollButton;
    if (retry instanceof HTMLButtonElement) {
      retry.hidden = !(options.showRetry && this.#pendingPoll?.taskId);
      retry.disabled = this.#busy;
    }
  }

  syncActions() {
    const hasFile = Boolean(this.#file);
    const hasStyle = Boolean(this.selectedStyle());
    const hasDesign = Boolean(this.#confirmedDesign);
    const ready =
      hasDesign && Boolean(this.#selectedProduct) && Boolean(this.#selectedVariant?.available) && !this.#busy;
    this.refs.nextStyleButton.disabled = this.#busy || this.#uploading || !this.#uploadId;
    this.refs.generateButton.disabled = this.#busy || !hasFile || !hasStyle;
    this.refs.clearButton.hidden = !hasFile;
    if (this.refs.backgroundEffectList instanceof HTMLElement) {
      this.refs.backgroundEffectList.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (input instanceof HTMLInputElement) input.disabled = this.#busy;
      });
    }
    this.syncPortraitTools();
    if (this.refs.nextProductButton instanceof HTMLButtonElement) {
      this.refs.nextProductButton.disabled = this.#busy || !hasDesign || !this.#selectedProduct;
    }
    this.refs.addToCartButton.disabled = !ready;
    this.refs.buyNowButton.disabled = !ready;
    this.refs.fileInput.disabled = this.#busy;
    this.refs.petNameInput.disabled = this.#busy;
  }

  /**
   * 工具行始终显示：生成中全部禁用。
   * 生成失败后也可点重新生成。
   */
  syncPortraitTools() {
    const canRetry =
      this.#step === 3 && Boolean(this.#file) && Boolean(this.selectedStyle()) && !this.#busy;
    this.setToolControl(this.refs.regenerateButton, canRetry);
  }

  /**
   * @param {HTMLElement | undefined} el
   * @param {boolean} enabled
   */
  setToolControl(el, enabled) {
    if (!el) return;
    el.hidden = false;
    if (el instanceof HTMLButtonElement) {
      el.disabled = !enabled;
      return;
    }
    el.classList.toggle('is-disabled', !enabled);
    el.setAttribute('aria-disabled', enabled ? 'false' : 'true');
    if (enabled) el.removeAttribute('tabindex');
    else el.setAttribute('tabindex', '-1');
  }

  selectedStyle() {
    return this.#selectedStyleId || '';
  }

  async loadStyles() {
    if (!this.#api) return;
    this.refs.styleGrid.innerHTML = `<p class="create-portrait__styles-loading">${this.settings.strings.loadingStyles}</p>`;
    try {
      const data = await this.#api.listStyles(this.settings.locale);
      this.#styleCategories = data.categories.filter((item) => item.id);
      this.#styles = data.styles.filter((item) => item.id);
      if (!this.#selectedStyleId && this.#styles.length) {
        this.#selectedStyleId = this.#styles[0].id;
      }
      this.renderStyleFilters();
      this.renderStyleGrid();
    } catch (error) {
      this.refs.styleFilters.replaceChildren();
      this.refs.styleGrid.innerHTML = `<p class="create-portrait__styles-empty">${this.errorMessage(error) || this.settings.strings.noStyles}</p>`;
      this.#styles = [];
      this.#selectedStyleId = '';
    } finally {
      this.syncActions();
    }
  }

  renderStyleFilters() {
    const filters = this.refs.styleFilters;
    filters.replaceChildren();
    this.#styleCategories.forEach((category) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'create-portrait__style-filter';
      button.dataset.category = category.id;
      button.textContent = category.name;
      button.classList.toggle('is-active', category.id === this.#styleCategory);
      button.setAttribute('aria-pressed', category.id === this.#styleCategory ? 'true' : 'false');
      button.addEventListener('click', () => this.handleStyleCategoryChange(category.id));
      filters.append(button);
    });
  }

  /**
   * @param {string} categoryId
   */
  handleStyleCategoryChange(categoryId) {
    this.#styleCategory = categoryId || 'all';
    this.renderStyleFilters();
    this.renderStyleGrid();
    this.syncActions();
  }

  filteredStyles() {
    if (this.#styleCategory === 'all') return this.#styles;
    return this.#styles.filter((style) => style.category === this.#styleCategory);
  }

  /**
   * @param {{ name?: string, previewUrl?: string }} style
   */
  createStylePreviewImage(style) {
    const fallback = this.settings.stylePlaceholder || DEFAULT_STYLE_PREVIEW_IMAGE;
    const src = String(style.previewUrl || '').trim() || fallback;
    const img = document.createElement('img');
    img.src = src;
    img.alt = style.name || '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.referrerPolicy = 'no-referrer';
    if (src === fallback) img.classList.add('is-placeholder');
    img.addEventListener('error', () => {
      if (img.dataset.placeholder === 'true') return;
      img.dataset.placeholder = 'true';
      img.src = fallback;
      img.classList.add('is-placeholder');
    });
    return img;
  }

  renderStyleGrid() {
    const list = this.refs.styleGrid;
    const styles = this.filteredStyles();
    list.replaceChildren();
    if (!styles.length) {
      list.innerHTML = `<p class="create-portrait__styles-empty">${this.settings.strings.noStyles}</p>`;
      return;
    }

    if (!styles.some((style) => style.id === this.#selectedStyleId)) {
      this.#selectedStyleId = styles[0].id;
    }

    const radioName = `create-portrait-style-${this.id || 'main'}`;
    styles.forEach((style, index) => {
      const inputId = `${radioName}-${style.id}-${index}`;
      const label = document.createElement('label');
      label.className = 'create-portrait__style-card';
      label.htmlFor = inputId;

      const input = document.createElement('input');
      input.id = inputId;
      input.className = 'create-portrait__style-input';
      input.type = 'radio';
      input.name = radioName;
      input.value = style.id;
      input.checked = style.id === this.#selectedStyleId;
      input.addEventListener('change', () => this.handleStyleChange(style.id));

      const preview = document.createElement('span');
      preview.className = 'create-portrait__style-preview';
      preview.append(this.createStylePreviewImage(style));
      const check = document.createElement('span');
      check.className = 'create-portrait__style-check';
      check.setAttribute('aria-hidden', 'true');
      check.textContent = '✓';
      preview.append(check);

      const name = document.createElement('span');
      name.className = 'create-portrait__style-name';
      name.textContent = style.name;

      label.append(input, preview, name);
      list.append(label);
    });
  }

  /**
   * @param {string} [styleId]
   */
  handleStyleChange(styleId) {
    if (styleId) this.#selectedStyleId = styleId;
    if (this.#confirmedDesign) {
      this.#confirmedDesign = null;
      this.#selectedProduct = null;
      this.refs.portraitResult.hidden = true;
    }
    this.syncActions();
  }

  bindBackgroundEffectInputs() {
    if (this.#backgroundEffectBound || !(this.refs.backgroundEffectList instanceof HTMLElement)) return;
    this.#backgroundEffectBound = true;
    this.refs.backgroundEffectList.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
      this.handleBackgroundEffectChange();
    });
  }

  handleBackgroundEffectChange() {
    const nextEffect = this.selectedBackgroundEffect();
    const prev = this.#backgroundEffect;
    if (prev.mode === nextEffect.mode && prev.color === nextEffect.color) return;
    this.#backgroundEffect = nextEffect;
    if (this.#confirmedDesign || this.#patterns.length || this.#pendingPoll) {
      this.resetDesign({ keepFile: true });
      this.setStatus('');
      if (this.#file) this.setStep(2);
    }
    this.syncActions();
  }

  /**
   * @returns {{ mode: 'original' | 'transparent' | 'color', color: string | null }}
   */
  selectedBackgroundEffect() {
    const list = this.refs.backgroundEffectList;
    if (!(list instanceof HTMLElement)) {
      return { mode: 'transparent', color: null };
    }
    const checked = list.querySelector('input[type="radio"]:checked');
    if (!(checked instanceof HTMLInputElement)) {
      return { mode: 'transparent', color: null };
    }
    const mode = String(checked.dataset.backgroundMode || checked.value || 'original').trim().toLowerCase();
    if (mode === 'color') {
      const color = String(checked.dataset.backgroundColor || checked.value || '').trim().toUpperCase();
      return color ? { mode: 'color', color } : { mode: 'transparent', color: null };
    }
    if (mode === 'transparent') {
      return { mode: 'transparent', color: null };
    }
    return { mode: 'original', color: null };
  }

  handlePetNameInput() {
    const nextName = this.petName();
    if (nextName === this.#petName) return;
    this.#petName = nextName;
    if (this.#confirmedDesign || this.#patterns.length || this.#pendingPoll) {
      this.resetDesign({ keepFile: true });
      this.setStatus('');
      if (this.#file) this.setStep(2);
    }
    this.syncActions();
  }

  handleFileChange = async (event) => {
    const input =
      event.currentTarget instanceof HTMLInputElement
        ? event.currentTarget
        : this.refs.fileInput;
    const file = input?.files?.[0];
    if (!file) return;

    this.resetDesign({ keepFile: true });
    this.#file = null;
    this.revokeObjectUrl();
    this.hideUploadPreview();
    this.setStep(1);
    this.setBusy(true);
    this.syncActions();

    try {
      this.#originalFile = file;
      const compression = await compressImageFile(file);
      const uploadFile = compression.file;
      this.#file = uploadFile;
      this.#objectUrl = URL.createObjectURL(uploadFile);
      this.showUploadPreview(this.#objectUrl);
      this.syncActions();
      if (compression.compressed && this.dataset.debug === 'true') {
        console.info('[create-portrait] compressed upload image', {
          originalSize: compression.originalSize,
          compressedSize: compression.compressedSize,
        });
      }
      this.refs.uploadSuccess.hidden = true;
      this.setStatus('');
      this.startUpload();
    } catch (error) {
      this.#uploadId = null;
      this.#uploading = false;
      this.#uploadFailed = false;
      this.syncUploadOverlays();
      this.setStatus(this.uploadErrorMessage(error), 'error');
    } finally {
      this.setBusy(false);
      this.syncActions();
    }
  };

  showUploadPreview(url) {
    this.refs.uploadZone?.classList.add('has-file');
    this.refs.uploadEmpty.hidden = true;
    this.refs.sourceImage.hidden = false;
    this.refs.sourceImage.src = url;
    this.refs.clearButton.hidden = false;
    this.refs.uploadSuccess.hidden = true;
    this.syncUploadOverlays();
  }

  hideUploadPreview() {
    this.#uploading = false;
    this.#uploadFailed = false;
    this.refs.uploadZone?.classList.remove('has-file');
    this.refs.uploadEmpty.hidden = false;
    this.refs.sourceImage.hidden = true;
    this.refs.sourceImage.removeAttribute('src');
    this.refs.clearButton.hidden = true;
    this.refs.uploadSuccess.hidden = true;
    this.syncUploadOverlays();
  }

  syncUploadOverlays() {
    const uploading = this.refs.uploadOverlayUploading;
    const retry = this.refs.uploadRetryButton;
    if (uploading instanceof HTMLElement) {
      uploading.hidden = !this.#uploading;
    }
    if (retry instanceof HTMLButtonElement) {
      retry.hidden = this.#uploading || !this.#uploadFailed;
      retry.disabled = this.#uploading || !this.#file;
    }
    this.refs.uploadZone?.classList.toggle('is-uploading', this.#uploading);
    this.refs.uploadZone?.classList.toggle('is-failed', this.#uploadFailed && !this.#uploading);
  }

  handleClear(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.resetDesign();
    this.setStep(1);
    this.setStatus('');
    // Ensure the same zone can accept a new file immediately.
    this.refs.fileInput.value = '';
    this.refs.fileInput.disabled = false;
    this.hideUploadPreview();
  }

  handleNextStyle() {
    if (!this.#uploadId || this.#uploading || this.#busy) return;
    this.setStep(2);
    this.refs.generateButton.focus({ preventScroll: true });
  }

  handlePreviousStep() {
    if (this.#busy) return;
    if (this.#step <= 1) return;
    if (this.#step === 4) {
      this.handleBackToProducts();
      return;
    }
    this.setStep(/** @type {1|2|3|4} */ (this.#step - 1));
  }

  async handleGenerate() {
    if (!this.#file || this.#busy || !this.#api) return;
    const style = this.selectedStyle();
    if (!style) return;

    this.setBusy(true);
    this.#pendingPoll = null;
    this.#patterns = [];
    this.#confirmedDesign = null;
    this.#matted = false;
    this.setPortraitCutout(false);
    this.#selectedProduct = null;
    this.refreshProductLists();
    this.setStep(3);
    this.refs.portraitEmpty.hidden = true;
    this.refs.portraitResult.hidden = false;
    this.refs.patternList.hidden = false;
    this.showGenerationProgress(this.settings.strings.generating, 8, 88);
    this.setStatus(this.settings.strings.generating);

    try {
      if (!this.#uploadId) {
        this.showGenerationProgress(this.settings.strings.uploading, 12, 36);
        this.setStatus(this.settings.strings.uploading);
        const result = await this.ensureUploaded();
        this.#uploadId = result.uploadId;
        if (result.sourceUrl?.startsWith('http')) this.refs.sourceImage.src = result.sourceUrl;
        this.refs.uploadSuccess.hidden = false;
        this.showGenerationProgress(this.settings.strings.generating, 38, 88);
        this.setStatus(this.settings.strings.generating);
      }

      if (this.#productPreviewsPromise) {
        try {
          await this.#productPreviewsPromise;
        } catch {
          /* print layouts are optional for generate */
        }
      }

      const petName = this.petName();
      const backgroundEffect = this.selectedBackgroundEffect();
      const { patterns, matted } = await this.#api.requestPatterns(this.#uploadId, style, this.#objectUrl || '', {
        count: 1,
        petName,
        backgroundEffect,
      });
      this.showGenerationProgress(this.settings.strings.generating, 92, 96);
      this.#patterns = patterns;
      this.#pendingPoll = null;
      const isTransparent = backgroundEffect.mode === 'transparent' || Boolean(matted);
      if (isTransparent || patterns.some((p) => p.matted)) {
        this.#matted = isTransparent;
        this.setPortraitCutout(isTransparent);
      } else {
        this.#matted = false;
        this.setPortraitCutout(false);
      }
      this.renderPatterns();
      if (patterns.length) {
        this.refs.portraitImage.src = patterns[0].previewUrl;
        this.setPortraitCutout(isTransparent);
        this.finishGenerationProgress();
        const doneMessage =
          backgroundEffect.mode === 'transparent'
            ? this.settings.strings.cutoutDone
            : backgroundEffect.mode === 'color'
              ? this.settings.strings.backgroundColorDone
              : this.settings.strings.confirmed;
        this.setStatus(doneMessage, 'success');
        await this.confirmPattern(patterns[0]);
      } else {
        this.stopGenerationProgress();
        this.setStatus(this.settings.strings.selectPattern);
      }
    } catch (error) {
      this.hideGenerationProgress(this.errorMessage(error) || this.settings.strings.errorGeneric, 'error');
      const taskId = error instanceof Error ? /** @type {Error & { taskId?: string }} */ (error).taskId : '';
      if (taskId) {
        this.#pendingPoll = {
          taskId,
          kind: 'patterns',
          style,
          petName: this.petName(),
          backgroundEffect: this.selectedBackgroundEffect(),
        };
      }
      this.setStatus(this.errorMessage(error), 'error', { showRetry: Boolean(this.#pendingPoll?.taskId) });
      this.refs.portraitResult.hidden = false;
      this.refs.portraitEmpty.hidden = true;
    } finally {
      this.stopGenerationProgress();
      this.setBusy(false);
      this.syncActions();
    }
  }

  renderPatterns() {
    const list = this.refs.patternList;
    list.replaceChildren();
    // Create page requests a single portrait and auto-confirms it; hide multi-select UI.
    if (this.#patterns.length <= 1) {
      list.hidden = true;
      return;
    }
    list.hidden = false;
    this.#patterns.forEach((pattern, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'create-portrait__pattern';
      button.dataset.patternId = pattern.id;
      const img = document.createElement('img');
      img.src = pattern.previewUrl;
      img.alt = `Portrait ${index + 1}`;
      img.loading = 'lazy';
      button.append(img);
      button.addEventListener('click', () => this.confirmPattern(pattern));
      list.append(button);
    });
  }

  async confirmPattern(pattern) {
    if (!this.#api) return;
    this.setBusy(true);
    try {
      const { design } = await this.#api.confirm(this.#uploadId || '', pattern, { petName: this.petName() });
      this.#pendingPoll = null;
      this.#confirmedDesign = design;
      if (pattern.matted || this.#matted) {
        this.#matted = true;
      }
      this.refs.portraitImage.src = design.previewUrl;
      this.setPortraitCutout(this.#matted);
      this.refs.portraitEmpty.hidden = true;
      this.refs.portraitResult.hidden = false;
      this.refs.patternList.querySelectorAll('.create-portrait__pattern').forEach((el) => {
        const selected = el instanceof HTMLElement && el.dataset.patternId === pattern.id;
        el.classList.toggle('is-selected', Boolean(selected));
      });
      this.refreshProductLists();
      this.setStatus(this.settings.strings.confirmed, 'success');
      if (this.#patterns.length <= 1) this.hideGenerationProgress();
      this.setStep(3);
      this.updatePortraitPrintGuide();
    } catch (error) {
      const taskId = error instanceof Error ? /** @type {Error & { taskId?: string }} */ (error).taskId : '';
      if (taskId) this.#pendingPoll = { taskId, kind: 'confirm', pattern: { ...pattern, petName: this.petName() } };
      if (this.#patterns.length <= 1) this.hideGenerationProgress(this.errorMessage(error) || this.settings.strings.errorGeneric, 'error');
      this.setStatus(this.errorMessage(error), 'error', { showRetry: Boolean(this.#pendingPoll?.taskId) });
    } finally {
      this.setBusy(false);
      this.syncActions();
    }
  }

  async handleRetryPoll() {
    const pending = this.#pendingPoll;
    if (!pending?.taskId || this.#busy || !this.#api) return;
    this.setBusy(true);
    this.setStatus(this.settings.strings.checkingTask);
    try {
      const completed = await this.#api.pollTask(pending.taskId);
      if (pending.kind === 'patterns') {
        const style = pending.style || this.selectedStyle();
        const raw = completed.patterns || completed.results || completed.items || [];
        const matted = Boolean(completed.matted) || raw.some((item) => Boolean(item.matted));
        const effectMode = String(completed.background_effect || pending.backgroundEffect?.mode || '').toLowerCase();
        const isTransparent = effectMode === 'transparent' || (matted && effectMode !== 'color');
        this.#patterns = raw.map((item, index) => ({
          id: String(item.pattern_id || item.id || `${style}-${index}`),
          previewUrl: String(item.preview_url || item.previewUrl || item.url),
          style,
          source: style === 'extract' ? 'extract' : `ai:${style}`,
          petName: pending.petName || this.petName(),
          matted: Boolean(item.matted) || matted,
        }));
        this.#pendingPoll = null;
        if (isTransparent) {
          this.#matted = true;
          this.setPortraitCutout(true);
        } else {
          this.#matted = false;
          this.setPortraitCutout(false);
        }
        this.renderPatterns();
        if (this.#patterns.length) {
          this.refs.portraitImage.src = this.#patterns[0].previewUrl;
          this.setPortraitCutout(this.#matted);
          await this.confirmPattern(this.#patterns[0]);
        }
        return;
      }
      if (pending.kind === 'matt') {
        const previewUrl = String(completed.preview_url || completed.previewUrl || '').trim();
        if (!previewUrl) throw new Error(this.settings.strings.errorGeneric);
        this.#pendingPoll = null;
        this.#matted = true;
        this.applyPortraitUrl(previewUrl, { matted: true });
        this.hideGenerationProgress(this.settings.strings.cutoutDone);
        this.setStatus(this.settings.strings.cutoutDone, 'success');
        return;
      }
      const pattern = pending.pattern;
      if (!pattern) throw new Error(this.settings.strings.errorGeneric);
      const designPayload = completed.design || completed;
      this.#confirmedDesign = {
        id: String(designPayload.design_id || designPayload.designId || pattern.id),
        previewUrl: String(designPayload.preview_url || designPayload.previewUrl || pattern.previewUrl),
        style: pattern.style,
        source: designPayload.source || pattern.source,
        petName: designPayload.pet_name || designPayload.petName || pattern.petName || this.petName(),
      };
      this.#pendingPoll = null;
      this.refs.portraitImage.src = this.#confirmedDesign.previewUrl;
      this.setStatus(this.settings.strings.confirmed, 'success');
    } catch (error) {
      this.setStatus(this.errorMessage(error), 'error', { showRetry: Boolean(this.#pendingPoll?.taskId) });
    } finally {
      this.setBusy(false);
      this.syncActions();
    }
  }

  handleRegenerate() {
    if (this.#busy || !this.#file || !this.selectedStyle()) return;
    this.#matted = false;
    this.setPortraitCutout(false);
    this.handleGenerate();
  }

  /**
   * @param {string} url
   * @param {{ matted?: boolean }} [options]
   */
  applyPortraitUrl(url, options = {}) {
    const previewUrl = String(url || '').trim();
    if (!previewUrl) return;
    const matted = Boolean(options.matted);
    this.refs.portraitImage.src = previewUrl;
    this.setPortraitCutout(matted);
    this.updatePortraitPrintGuide();
    if (this.#confirmedDesign) {
      this.#confirmedDesign = { ...this.#confirmedDesign, previewUrl };
    }
    if (this.#patterns.length) {
      this.#patterns = this.#patterns.map((pattern, index) =>
        index === 0 ? { ...pattern, previewUrl } : pattern
      );
    }
    this.refreshProductLists();
  }

  /** @param {boolean} matted */
  setPortraitCutout(matted) {
    this.refs.portraitImage?.closest('.create-portrait__portrait-frame')?.classList.toggle('is-cutout', matted);
  }

  /**
   * @param {HTMLElement} list
   * @param {CreateProduct[]} products
   */
  renderProducts(list, products) {
    list.replaceChildren();
    const items = products.filter((product) => !String(product.id).startsWith('pending:'));
    if (!items.length) {
      if (this.#productLoading) {
        list.innerHTML = `<p class="create-portrait__products-empty">${this.settings.strings.loadingProducts}</p>`;
      } else {
        list.innerHTML = `<p class="create-portrait__products-empty">${this.settings.strings.noProducts}</p>`;
      }
      return;
    }
    this.appendProducts(list, items);
  }

  /**
   * @param {HTMLElement} list
   * @param {CreateProduct[]} products
   */
  appendProducts(list, products) {
    const enabled = Boolean(this.#confirmedDesign);
    products.forEach((product) => {
      if (String(product.id).startsWith('pending:')) return;
      const button = this.createProductButton(product, enabled);
      list.append(button);
    });
  }

  /**
   * @param {CreateProduct} product
   * @param {boolean} enabled
   */
  createProductButton(product, enabled) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'create-portrait__product';
    button.disabled = !enabled;
    button.dataset.productId = String(product.id);
    button.dataset.productHandle = String(product.handle || '');
    button.classList.toggle('is-selected', String(this.#selectedProduct?.id) === String(product.id));
    const media = document.createElement('span');
    media.className = 'create-portrait__product-media';
    const meta = document.createElement('span');
    meta.className = 'create-portrait__product-meta';
    const title = document.createElement('span');
    title.className = 'create-portrait__product-title';
    title.textContent = product.title;
    meta.append(title);
    button.append(media, meta);
    this.syncProductCard(button, product);
    button.addEventListener('click', () => this.selectProduct(product));
    return button;
  }

  /**
   * @param {CreateProduct[]} products
   */
  updateProductCards(products) {
    products.forEach((product) => {
      const list = this.refs.productList;
      if (!(list instanceof HTMLElement)) return;
      const button = list.querySelector(`.create-portrait__product[data-product-id="${CSS.escape(String(product.id))}"]`);
      if (!(button instanceof HTMLElement)) return;
      this.syncProductCard(button, product);
    });
  }

  setupProductInfiniteScroll() {
    const sentinel = this.refs.productListSentinel;
    if (!(sentinel instanceof HTMLElement)) return;
    this.#productLoadObserver?.disconnect();
    this.#productLoadObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        void this.loadMoreProducts();
      },
      { root: null, rootMargin: '240px 0px', threshold: 0 }
    );
    this.#productLoadObserver.observe(sentinel);
  }

  setupModalProductInfiniteScroll() {
    const sentinel = this.refs.modalProductListSentinel;
    if (!(sentinel instanceof HTMLElement)) return;
    const modal = this.refs.productsModal;
    const root =
      modal instanceof HTMLElement
        ? modal.querySelector('.create-portrait__products-modal-inner')
        : null;
    this.#modalProductLoadObserver?.disconnect();
    this.#modalProductLoadObserver = new IntersectionObserver(
      (entries) => {
        if (!this.#productsModalOpen) return;
        if (!entries.some((entry) => entry.isIntersecting)) return;
        void this.loadMoreProducts();
      },
      {
        root: root instanceof Element ? root : null,
        rootMargin: '160px 0px',
        threshold: 0,
      }
    );
    this.#modalProductLoadObserver.observe(sentinel);
  }

  catalogProducts() {
    return this.#products.filter((product) => !String(product.id).startsWith('pending:'));
  }

  updateModalProductLoadStatus() {
    const status = this.refs.modalProductLoadStatus;
    if (!(status instanceof HTMLElement)) return;
    if (this.#productLoading) {
      status.hidden = false;
      status.textContent = this.settings.strings.loadingProducts;
      return;
    }
    status.hidden = true;
    status.textContent = '';
  }

  renderModalProducts() {
    const list = this.refs.modalProductList;
    if (!(list instanceof HTMLElement)) return;
    list.replaceChildren();
    const products = this.catalogProducts();
    if (!products.length) {
      const empty = document.createElement('p');
      empty.className = 'create-portrait__products-empty';
      empty.textContent = this.#productLoading
        ? this.settings.strings.loadingProducts
        : this.settings.strings.noProducts;
      list.append(empty);
      return;
    }
    this.appendModalProducts(products);
  }

  /**
   * @param {CreateProduct[]} products
   */
  appendModalProducts(products) {
    const list = this.refs.modalProductList;
    if (!(list instanceof HTMLElement)) return;
    products.forEach((product) => {
      if (String(product.id).startsWith('pending:')) return;
      if (list.querySelector(`[data-product-id="${CSS.escape(String(product.id))}"]`)) return;
      list.append(this.createModalProductButton(product));
    });
    this.markSelectedProduct();
  }

  /**
   * @param {CreateProduct} product
   */
  createModalProductButton(product) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'create-portrait__modal-product';
    button.dataset.productId = String(product.id);
    button.dataset.productHandle = String(product.handle || '');
    button.classList.toggle(
      'is-selected',
      String(this.#selectedProduct?.id) === String(product.id) ||
        String(this.#selectedProduct?.handle || '').toLowerCase() === String(product.handle || '').toLowerCase()
    );
    const media = document.createElement('span');
    media.className = 'create-portrait__modal-product-media';
    const imageUrl = this.productCatalogImage(product);
    if (imageUrl) {
      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = product.title || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      media.append(img);
    }
    const title = document.createElement('span');
    title.className = 'create-portrait__modal-product-title';
    title.textContent = product.title;
    button.append(media, title);
    button.addEventListener('click', () => this.handleModalProductSelect(product));
    return button;
  }

  handleOpenProductsModal() {
    const modal = this.refs.productsModal;
    if (!(modal instanceof HTMLDialogElement)) return;
    this.#productsModalOpen = true;
    this.renderModalProducts();
    this.updateModalProductLoadStatus();
    if (!this.catalogProducts().length) void this.ensureProductsLoaded();
    if (typeof modal.showModal === 'function') modal.showModal();
    else modal.setAttribute('open', '');
  }

  /**
   * Show catalog images of the currently selected product (same style as step 4 gallery).
   */
  handleOpenSelectedProductImages() {
    const product = this.#selectedProduct || this.preferredProduct();
    if (!product) return;
    const modal = this.refs.productImagesModal;
    const list = this.refs.productImagesModalList;
    const title = this.refs.productImagesModalTitle;
    if (!(modal instanceof HTMLDialogElement) || !(list instanceof HTMLElement)) return;
    if (title instanceof HTMLElement) {
      title.textContent = product.title || this.settings.strings.productImages;
    }
    list.replaceChildren();
    const urls = this.selectedProductImageUrls(product);
    if (!urls.length) {
      const empty = document.createElement('p');
      empty.className = 'create-portrait__products-empty';
      empty.textContent = this.settings.strings.noProducts;
      list.append(empty);
    } else {
      urls.forEach((src) => {
        const figure = document.createElement('figure');
        figure.className = 'create-portrait__detail-product-image';
        const image = document.createElement('img');
        image.src = src;
        image.alt = product.title || '';
        image.loading = 'lazy';
        image.decoding = 'async';
        figure.append(image);
        list.append(figure);
      });
    }
    if (typeof modal.showModal === 'function') modal.showModal();
    else modal.setAttribute('open', '');
  }

  /**
   * @param {CreateProduct | null | undefined} product
   * @returns {string[]}
   */
  selectedProductImageUrls(product) {
    if (!product) return [];
    const urls = [];
    const seen = new Set();
    const add = (value) => {
      const url = this.absoluteImageUrl(value);
      if (!url || seen.has(url)) return;
      seen.add(url);
      urls.push(url);
    };
    (product.images || []).forEach((image) => add(image?.src));
    add(product.catalogImage);
    add(product.featuredImage);
    return urls;
  }

  handleCloseProductImagesModal() {
    const modal = this.refs.productImagesModal;
    if (!(modal instanceof HTMLDialogElement)) return;
    if (typeof modal.close === 'function') modal.close();
    else modal.removeAttribute('open');
  }

  /**
   * @param {Event} event
   */
  handleProductImagesModalClick(event) {
    const modal = this.refs.productImagesModal;
    if (!(modal instanceof HTMLDialogElement)) return;
    if (event.target === modal) this.handleCloseProductImagesModal();
  }

  handleProductImagesModalClose() {
    /* no-op placeholder for dialog close hook */
  }

  handleCloseProductsModal() {
    const modal = this.refs.productsModal;
    if (!(modal instanceof HTMLDialogElement)) return;
    if (typeof modal.close === 'function') modal.close();
    else modal.removeAttribute('open');
    this.#productsModalOpen = false;
  }

  /**
   * @param {Event} event
   */
  handleProductsModalClick(event) {
    const modal = this.refs.productsModal;
    if (!(modal instanceof HTMLDialogElement)) return;
    if (event.target === modal) this.handleCloseProductsModal();
  }

  handleProductsModalClose() {
    this.#productsModalOpen = false;
  }

  /**
   * @param {CreateProduct} product
   */
  handleModalProductSelect(product) {
    const handle = String(product.handle || '').trim();
    if (handle) {
      this.#preferredProductHandle = handle;
      this.#preferredVariantId = '';
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('product', handle);
        url.searchParams.delete('template');
        url.searchParams.delete('variant');
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      } catch {
        /* ignore */
      }
      try {
        sessionStorage.setItem(
          'pettaa-preferred-product',
          JSON.stringify({
            handle,
            title: product.title || '',
            image: this.productCatalogImage(product) || '',
          })
        );
      } catch {
        /* ignore */
      }
    }
    this.selectProduct(product, { requireDesign: false, silent: true });
    this.updatePreferredProductNotice();
    this.handleCloseProductsModal();
  }

  async ensureProductsLoaded() {
    if (this.#productLoading) return;
    if (!this.#productHasMore && this.#productPage > 0) return;
    if (this.catalogProducts().length) return;
    await this.loadMoreProducts();
  }

  updateProductLoadStatus() {
    const status = this.refs.productLoadStatus;
    if (!(status instanceof HTMLElement)) return;
    if (this.#productLoading) {
      status.hidden = false;
      status.textContent = this.settings.strings.loadingProducts;
      return;
    }
    status.hidden = true;
    status.textContent = '';
  }

  async loadMoreProducts() {
    if (this.#productLoading || !this.#productHasMore) return;
    this.#productLoading = true;
    this.updateProductLoadStatus();
    this.updateModalProductLoadStatus();
    if (!this.#products.length && this.refs.productList instanceof HTMLElement) {
      this.renderProducts(this.refs.productList, []);
    }
    try {
      const page = this.#productPage + 1;
      const handle = this.settings.collectionHandle || 'all';
      const response = await fetch(
        `/collections/${encodeURIComponent(handle)}/products.json?limit=${PRODUCT_PAGE_SIZE}&page=${page}`,
        { signal: this.#lifecycle.signal }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const raw = Array.isArray(data.products) ? data.products : [];
      const items = raw.map((item) => this.normalizeShopifyProduct(item)).filter(Boolean);
      if (!items.length) {
        this.#productHasMore = false;
        if (!this.#products.length && this.refs.productList instanceof HTMLElement) {
          this.renderProducts(this.refs.productList, []);
        }
        if (this.#productsModalOpen) this.renderModalProducts();
        return;
      }
      this.#productPage = page;
      const existingIds = new Set(
        this.#products.filter((product) => !String(product.id).startsWith('pending:')).map((product) => String(product.id))
      );
      const existingHandles = new Set(
        this.#products
          .filter((product) => !String(product.id).startsWith('pending:'))
          .map((product) => String(product.handle || '').toLowerCase())
          .filter(Boolean)
      );
      const newItems = items.filter(
        (product) =>
          !existingIds.has(String(product.id)) &&
          !existingHandles.has(String(product.handle || '').toLowerCase())
      );
      const incomingHandles = new Set(newItems.map((product) => String(product.handle || '').toLowerCase()));
      this.#products = [
        ...this.#products.filter((product) => {
          if (!String(product.id).startsWith('pending:')) return true;
          return !incomingHandles.has(String(product.handle || '').toLowerCase());
        }),
        ...newItems,
      ];
      if (page === 1) {
        this.promotePreferredProduct();
        if (this.refs.productList instanceof HTMLElement) {
          this.renderProducts(this.refs.productList, this.#products);
        }
        this.applyPreferredProduct({ requireDesign: false, silent: true });
        if (this.#productsModalOpen) this.renderModalProducts();
      } else if (newItems.length && this.refs.productList instanceof HTMLElement) {
        this.appendProducts(this.refs.productList, newItems);
        if (this.#productsModalOpen) this.appendModalProducts(newItems);
      }
      if (items.length < PRODUCT_PAGE_SIZE) {
        this.#productHasMore = false;
      }
      if (newItems.length) {
        void this.loadProductPreviews(newItems);
      }
      this.syncActions();
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.info('[create-portrait] load products failed', error);
        this.#productHasMore = false;
      }
    } finally {
      this.#productLoading = false;
      this.updateProductLoadStatus();
      this.updateModalProductLoadStatus();
    }
  }

  refreshProductLists() {
    this.applyPreferredProduct({ requireDesign: false, silent: true });
    if (this.refs.productList instanceof HTMLElement) {
      this.renderProducts(this.refs.productList, this.#products);
    }
    if (this.#productsModalOpen) this.renderModalProducts();
    this.syncActions();
  }

  /**
   * @param {CreateProduct} product
   * @param {HTMLElement} media
   */
  mountProductCardPreview(product, media) {
    this.mountPrintPreview(media, {
      mockupUrl: this.productDesignImage(product),
      portraitUrl: this.#confirmedDesign?.previewUrl || '',
      layout: this.printLayoutForProduct(product),
      alt: product.title,
    });
  }

  /**
   * @param {HTMLElement} button
   * @param {CreateProduct} product
   */
  syncProductPreviewIssue(button, product) {
    const reason = this.printPreviewIssue(product);
    const badgeMode = this.isBadgePreviewMode(product);
    button.classList.toggle('create-portrait__product--preview-issue', Boolean(reason));
    button.classList.toggle('create-portrait__product--badge-mode', badgeMode && Boolean(reason));
    let issue = button.querySelector('.create-portrait__product-preview-issue');
    if (!reason) {
      issue?.remove();
      return;
    }
    const media = button.querySelector('.create-portrait__product-media');
    if (!(media instanceof HTMLElement)) return;
    if (!issue) {
      issue = document.createElement('span');
      issue.className = 'create-portrait__product-preview-issue';
      media.append(issue);
    }
    issue.textContent = reason;
  }

  /**
   * @param {CreateProduct[]} products
   */
  updateProductPreviewIssues(products) {
    const list = this.refs.productList;
    if (!(list instanceof HTMLElement)) return;
    products.forEach((product) => {
      const button = list.querySelector(
        `.create-portrait__product[data-product-id="${CSS.escape(String(product.id))}"]`
      );
      if (!(button instanceof HTMLElement)) return;
      this.syncProductPreviewIssue(button, product);
    });
  }

  /**
   * @param {HTMLElement} button
   * @param {CreateProduct} product
   */
  syncProductCard(button, product) {
    const media = button.querySelector('.create-portrait__product-media');
    if (!(media instanceof HTMLElement)) return;
    this.mountProductCardPreview(product, media);
    this.syncProductPreviewIssue(button, product);
  }

  /**
   * @param {HTMLElement} container
   * @param {{ mockupUrl?: string, portraitUrl?: string, layout?: CreateProduct['printLayout'], alt?: string }} opts
   */
  mountPrintPreview(container, opts) {
    container.replaceChildren();
    const mockupUrl = String(opts.mockupUrl || '').trim();
    const portraitUrl = String(opts.portraitUrl || '').trim();
    const layout = opts.layout || null;
    const overlays = this.pasteOverlaysForLayout(layout);
    // Keep list card media height uniform; do not inherit per-product canvas ratio.
    container.style.removeProperty('aspect-ratio');
    const stage = document.createElement('span');
    stage.className = 'create-portrait__product-stage';
    if (mockupUrl) {
      const img = document.createElement('img');
      img.className = 'create-portrait__product-mockup';
      img.src = mockupUrl;
      img.alt = opts.alt || '';
      img.loading = 'lazy';
      stage.append(img);
    }
    if (portraitUrl && overlays.length) {
      overlays.forEach((overlay, index) => {
        const print = document.createElement('img');
        print.className = 'create-portrait__product-print';
        print.src = portraitUrl;
        print.alt = '';
        print.style.left = `${Number(overlay.x) * 100}%`;
        print.style.top = `${Number(overlay.y) * 100}%`;
        print.style.width = `${Number(overlay.w) * 100}%`;
        print.style.height = `${Number(overlay.h) * 100}%`;
        const layerIndex = Number(overlay.layer_index ?? overlay.layerIndex);
        print.style.zIndex = String(10 + (Number.isFinite(layerIndex) ? layerIndex : index));
        const clip = overlay.clip_path || overlay.clipPath || '';
        if (clip) print.style.clipPath = clip;
        stage.append(print);
      });
    } else if (portraitUrl) {
      const badge = document.createElement('img');
      badge.className = 'create-portrait__product-badge';
      badge.src = portraitUrl;
      badge.alt = '';
      stage.append(badge);
    }
    container.append(stage);
  }

  handleToggleFavorite() {
    this.#favorited = !this.#favorited;
    const btn = this.refs.favoriteButton;
    if (btn instanceof HTMLButtonElement) {
      btn.setAttribute('aria-pressed', this.#favorited ? 'true' : 'false');
      btn.classList.toggle('is-active', this.#favorited);
    }
  }

  /**
   * @param {CreateProduct} product
   * @param {{ requireDesign?: boolean, silent?: boolean }} [options]
   */
  selectProduct(product, options = {}) {
    const requireDesign = options.requireDesign !== false;
    const silent = Boolean(options.silent);
    if (requireDesign && !this.#confirmedDesign) {
      if (!silent) this.setStatus(this.settings.strings.selectProduct, 'error');
      return;
    }
    const sameProduct = String(this.#selectedProduct?.id || '') === String(product.id);
    const preferredVariantId = sameProduct && this.#selectedVariant
      ? String(this.#selectedVariant.id)
      : String(product.handle || '').toLowerCase() === this.#preferredProductHandle.toLowerCase()
        ? this.#preferredVariantId
        : '';
    const initialVariant = product.variants.find((variant) => String(variant.id) === preferredVariantId)
      || product.variants.find((variant) => variant.available)
      || product.variants[0];
    this.#selectedProduct = product;
    this.updatePreferredProductNotice();
    this.#optionSelections = {};
    (product.options || []).forEach((option, index) => {
      this.#optionSelections[option.name] = initialVariant?.[`option${index + 1}`] || option.values[0] || '';
    });
    this.resolveVariant();
    this.markSelectedProduct();
    if (this.#step === 4) this.renderProductDetail();
    if (!silent) this.setStatus(this.settings.strings.selectProductContinue || this.settings.strings.selectProduct);
    this.updatePortraitPrintGuide();
    this.syncActions();
  }

  markSelectedProduct() {
    const selectedId = String(this.#selectedProduct?.id || '');
    const selectedHandle = String(this.#selectedProduct?.handle || '').toLowerCase();
    const list = this.refs.productList;
    if (list instanceof HTMLElement) {
      list.querySelectorAll('.create-portrait__product').forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        const match =
          el.dataset.productId === selectedId ||
          String(el.dataset.productHandle || '').toLowerCase() === selectedHandle;
        el.classList.toggle('is-selected', match);
      });
    }
    const modalList = this.refs.modalProductList;
    if (modalList instanceof HTMLElement) {
      modalList.querySelectorAll('.create-portrait__modal-product').forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        const match =
          el.dataset.productId === selectedId ||
          String(el.dataset.productHandle || '').toLowerCase() === selectedHandle;
        el.classList.toggle('is-selected', match);
      });
    }
  }

  handleNextProduct() {
    if (!this.#confirmedDesign || !this.#selectedProduct || this.#busy) return;
    this.resolveVariant();
    this.renderProductDetail();
    this.setStep(4);
    this.syncActions();
  }

  handleBackToProducts() {
    this.setStep(3);
    this.markSelectedProduct();
    this.syncActions();
  }

  renderProductDetail() {
    const product = this.#selectedProduct;
    if (!product) return;
    this.refs.purchaseTitle.textContent = product.title;
    this.renderDetailDesignPreview();
    this.renderVariantOptions();
    this.syncDetailPrintOverlay();
    this.renderDetailDescription();
    this.updatePurchasePrice();
    this.refs.quantityInput.value = '1';
  }

  /**
   * @param {CreateProduct | null | undefined} product
   */
  mockupImageForProduct(product) {
    if (!product) return '';
    const layout = this.printLayoutForProduct(product);
    return (
      layout?.mockup_url ||
      layout?.mockupUrl ||
      this.productDesignImage(product) ||
      product.featuredImage ||
      ''
    );
  }

  /**
   * @param {CreateProduct | null | undefined} product
   * @returns {CreateProductImage[]}
   */
  collectProductImages(product) {
    if (!product) return [];
    /** @type {CreateProductImage[]} */
    const images = [];
    const seen = new Set();
    const add = (src, variantIds = []) => {
      const url = this.absoluteImageUrl(src);
      if (!url || seen.has(url)) return;
      seen.add(url);
      images.push({
        src: url,
        variantIds: [...new Set(variantIds.map(Number))].filter(Boolean),
      });
    };
    (product.images || []).forEach((image) => add(image.src, image.variantIds || []));
    if (product.featuredImage) add(product.featuredImage);
    (product.variants || []).forEach((variant) => {
      if (variant.featuredImage) add(variant.featuredImage, [Number(variant.id)]);
    });
    return images;
  }

  /**
   * @param {CreateProduct | null | undefined} product
   * @returns {CreateProductImage[]}
   */
  imagesForCurrentVariant(product) {
    const all = this.collectProductImages(product);
    const variant = this.#selectedVariant;
    if (!variant) return all;
    const variantId = Number(variant.id);
    const filtered = all.filter((image) => {
      const ids = image.variantIds || [];
      return !ids.length || ids.includes(variantId);
    });
    return filtered.length ? filtered : all;
  }

  /**
   * Product photos only (exclude POD mockup used by detailImage).
   * @returns {string[]}
   */
  detailProductImageUrls() {
    const product = this.#selectedProduct;
    if (!product) return [];
    const mockupUrl = this.absoluteImageUrl(this.mockupImageForProduct(product)) || '';
    return this.imagesForCurrentVariant(product)
      .map((image) => image.src)
      .filter((src) => src && src !== mockupUrl);
  }

  sanitizeProductDescription(html) {
    const raw = String(html || '').trim();
    if (!raw) return '';
    const template = document.createElement('template');
    template.innerHTML = raw;
    template.content.querySelectorAll('script, style, iframe, object, embed').forEach((el) => el.remove());
    template.content.querySelectorAll('*').forEach((el) => {
      [...el.attributes].forEach((attr) => {
        const name = attr.name.toLowerCase();
        const value = String(attr.value || '').trim();
        if (name.startsWith('on')) el.removeAttribute(attr.name);
        if ((name === 'href' || name === 'src') && /^javascript:/i.test(value)) {
          el.removeAttribute(attr.name);
        }
      });
    });
    return template.innerHTML.trim();
  }

  renderDetailDescriptionBody() {
    const body = this.refs.detailDescriptionBody;
    if (!(body instanceof HTMLElement)) return false;
    body.replaceChildren();
    const html = this.sanitizeProductDescription(this.#selectedProduct?.descriptionHtml || '');
    if (!html) {
      body.hidden = true;
      return false;
    }
    body.innerHTML = html;
    body.hidden = false;
    return true;
  }

  renderDetailDescription() {
    const section = this.refs.detailDescription;
    const hasDescription = this.renderDetailDescriptionBody();
    const hasImages = this.renderDetailProductImages();
    if (section instanceof HTMLElement) section.hidden = !hasDescription && !hasImages;
  }

  renderDetailProductImages() {
    const container = this.refs.detailGallery;
    const title = this.refs.detailGalleryTitle;
    const note = this.refs.detailGalleryNote;
    if (!(container instanceof HTMLElement)) return false;
    container.replaceChildren();
    const urls = this.detailProductImageUrls();
    if (!urls.length) {
      if (title instanceof HTMLElement) title.hidden = true;
      if (note instanceof HTMLElement) note.hidden = true;
      container.hidden = true;
      return false;
    }
    if (title instanceof HTMLElement) title.hidden = false;
    if (note instanceof HTMLElement) note.hidden = false;
    container.hidden = false;
    container.setAttribute('aria-label', this.settings.strings.productImages);
    urls.forEach((src) => {
      const figure = document.createElement('figure');
      figure.className = 'create-portrait__detail-product-image';
      const image = document.createElement('img');
      image.src = src;
      image.alt = this.#selectedProduct?.title || '';
      image.loading = 'lazy';
      image.decoding = 'async';
      figure.append(image);
      container.append(figure);
    });
    return true;
  }

  syncDetailPrintOverlay() {
    const img = this.refs.detailImage;
    const media = img?.parentElement;
    if (!(media instanceof HTMLElement)) return;
    media.querySelectorAll('.create-portrait__product-print').forEach((el) => el.remove());
    const product = this.#selectedProduct;
    const layout = this.printLayoutForProduct(product);
    const mockupUrl = this.mockupImageForProduct(product);
    const fallback = this.#confirmedDesign?.previewUrl || '';
    img.src = mockupUrl || fallback;
    img.alt = product?.title || '';
    const canvas = layout?.canvas;
    if (canvas?.width && canvas?.height) {
      media.style.aspectRatio = `${canvas.width} / ${canvas.height}`;
    } else {
      media.style.removeProperty('aspect-ratio');
    }
    img.classList.add('create-portrait__product-mockup');
    const overlays = this.pasteOverlaysForLayout(layout);
    const portraitUrl = this.#confirmedDesign?.previewUrl || '';
    if (!portraitUrl || !overlays.length) return;
    overlays.forEach((overlay, index) => {
      const print = document.createElement('img');
      print.className = 'create-portrait__product-print';
      print.src = portraitUrl;
      print.alt = '';
      print.style.left = `${Number(overlay.x) * 100}%`;
      print.style.top = `${Number(overlay.y) * 100}%`;
      print.style.width = `${Number(overlay.w) * 100}%`;
      print.style.height = `${Number(overlay.h) * 100}%`;
      const layerIndex = Number(overlay.layer_index ?? overlay.layerIndex);
      print.style.zIndex = String(10 + (Number.isFinite(layerIndex) ? layerIndex : index));
      const clip = overlay.clip_path || overlay.clipPath || '';
      if (clip) print.style.clipPath = clip;
      media.append(print);
    });
  }

  renderVariantOptions() {
    const product = this.#selectedProduct;
    const container = this.refs.variantOptions;
    container.replaceChildren();
    if (!product) return;

    product.options.forEach((option) => {
      if (option.values.length <= 1 && option.values[0] === 'Default Title') return;
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'create-portrait__option';
      const legend = document.createElement('legend');
      legend.textContent = option.name;
      fieldset.append(legend);
      const row = document.createElement('div');
      row.className = 'create-portrait__option-values';
      row.setAttribute('role', 'group');
      row.setAttribute('aria-label', option.name);

      option.values.forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'create-portrait__option-chip';
        button.textContent = value;
        const selected = this.#optionSelections[option.name] === value;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.selectOptionValue(option.name, value, row);
        });
        row.append(button);
      });
      fieldset.append(row);
      container.append(fieldset);
    });
  }

  renderDetailDesignPreview() {
    const design = this.#confirmedDesign;
    if (!design?.previewUrl) {
      this.refs.detailDesignPreview.hidden = true;
      this.refs.detailDesignImage.removeAttribute('src');
      this.refs.detailPetName.hidden = true;
      this.refs.detailPetName.textContent = '';
      return;
    }
    const petName = design.petName || this.petName();
    this.refs.detailDesignPreview.hidden = false;
    this.refs.detailDesignPreview.setAttribute('aria-label', petName ? `View pet image preview for ${petName}` : 'View pet image preview');
    this.refs.detailDesignImage.src = design.previewUrl;
    this.refs.detailDesignImage.alt = petName || 'Generated pet portrait';
    this.refs.detailPetName.hidden = !petName;
    this.refs.detailPetName.textContent = petName ? `Pet name: ${petName}` : '';
  }

  handleDetailDesignPreview() {
    const design = this.#confirmedDesign;
    if (!design?.previewUrl) return;
    this.openDesignLightbox(design.previewUrl, design.petName || this.petName());
  }

  /**
   * @param {string} src
   * @param {string} [alt]
   */
  openDesignLightbox(src, alt = '') {
    if (!src) return;
    this.refs.lightboxImage.src = src;
    this.refs.lightboxImage.alt = alt || 'Generated pet portrait';
    if (!this.refs.lightbox.open) this.refs.lightbox.showModal();
  }

  selectOptionValue(optionName, value, row) {
    const scrollRoot = this.getScrollRoot();
    const saved = scrollRoot.scrollTop;
    this.#optionSelections[optionName] = value;
    row.querySelectorAll('.create-portrait__option-chip').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const selected = el.textContent === value;
      el.classList.toggle('is-selected', selected);
      el.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
    this.resolveVariant();
    this.updatePurchasePrice();
    this.syncActions();
    this.syncDetailPrintOverlay();
    this.renderDetailDescription();
    requestAnimationFrame(() => {
      scrollRoot.scrollTop = saved;
    });
  }

  getScrollRoot() {
    const pageWrapper = document.querySelector('.page-wrapper');
    if (pageWrapper instanceof HTMLElement && window.matchMedia('(min-width: 990px)').matches) {
      return pageWrapper;
    }
    return document.scrollingElement || document.documentElement;
  }

  resolveVariant() {
    const product = this.#selectedProduct;
    if (!product) {
      this.#selectedVariant = null;
      return;
    }
    const values = product.options.map((option) => this.#optionSelections[option.name] || null);
    this.#selectedVariant =
      product.variants.find((variant) => {
        const opts = [variant.option1, variant.option2, variant.option3];
        return values.every((value, index) => product.options[index] == null || opts[index] === value);
      }) || null;
  }

  updatePurchasePrice() {
    const variant = this.#selectedVariant;
    if (!variant) {
      this.refs.purchasePrice.textContent = this.settings.strings.unavailable;
      return;
    }
    this.refs.purchasePrice.textContent = variant.available
      ? variant.priceFormatted || this.formatPrice(variant.price)
      : this.settings.strings.soldOut;
  }

  formatPrice(cents) {
    try {
      return formatMoney(cents, this.settings.moneyFormat, this.settings.currency);
    } catch {
      return `$${(Number(cents) / 100).toFixed(2)}`;
    }
  }

  handleQtyMinus() {
    const input = this.refs.quantityInput;
    input.value = String(Math.max(1, (Number(input.value) || 1) - 1));
  }

  handleQtyPlus() {
    const input = this.refs.quantityInput;
    input.value = String(Math.max(1, (Number(input.value) || 1) + 1));
  }

  handleAddToCart() {
    return this.addToCart({ checkout: false });
  }

  handleBuyNow() {
    return this.addToCart({ checkout: true });
  }

  async addToCart(options) {
    const design = this.#confirmedDesign;
    const variant = this.#selectedVariant;
    if (!design || !variant?.available || this.#busy) return;

    const quantity = Math.max(1, Number(this.refs.quantityInput.value) || 1);
    this.setBusy(true);
    this.setStatus(this.settings.strings.adding);

    const formData = new FormData();
    formData.append('id', String(variant.id));
    formData.append('quantity', String(quantity));
    const portraitUrl = design.previewUrl.startsWith('blob:') ? '' : design.previewUrl;
    let preview = portraitUrl || 'Design confirmed';
    let previewFile = null;
    let composedUrl = '';
    const layout = this.printLayoutForProduct(this.#selectedProduct);
    const mockupUrl = String(layout?.mockup_url || layout?.mockupUrl || this.productDesignImage(this.#selectedProduct) || '').trim();
    const overlays = this.pasteOverlaysForLayout(layout);
    if (this.#api && mockupUrl && portraitUrl && overlays.length) {
      try {
        const composed = await this.#api.composePreview({
          mockupUrl,
          portraitUrl,
          overlay: overlays[0],
          overlays,
        });
        composedUrl = composed?.previewUrl || '';
        if (composed?.previewBase64) {
          previewFile = base64ToJpegFile(composed.previewBase64, composed.mime);
        }
        if (composedUrl) preview = composedUrl;
      } catch (error) {
        console.info('[create-portrait] compose preview failed', error);
      }
    }
    if (previewFile) {
      formData.append('properties[_tib_design_link_1]', previewFile, previewFile.name);
    } else {
      formData.append('properties[_tib_design_link_1]', preview);
    }
    formData.append('properties[_customization_image]', portraitUrl);
    const productId = String(this.#selectedProduct?.id || '')
      .replace(/^gid:\/\/shopify\/Product\//i, '')
      .trim();
    const variantId = String(variant.id || '')
      .replace(/^gid:\/\/shopify\/ProductVariant\//i, '')
      .trim();
    if (productId) formData.append('properties[_product_id]', productId);
    if (variantId) formData.append('properties[_variant_id]', variantId);
    const petName = design.petName || this.petName();
    if (petName) formData.append('properties[_pet_name]', petName);

    if (options.checkout) {
      console.log('[create-portrait] Buy Now hidden properties', lineItemPropertiesFromFormData(formData));
    }

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variant.id), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(cartAddUrlForPreview(Boolean(previewFile)), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const payload = await response.text();
      let data = {};
      try {
        data = payload ? JSON.parse(payload) : {};
      } catch {
        data = {};
      }
      if (!response.ok || data.status) {
        const message = data.description || data.message || this.settings.strings.errorGeneric;
        deferred.reject(new Error(message));
        throw new Error(message);
      }
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          sections: data.sections,
          source: 'create-portrait-component',
          itemCount: quantity,
        },
      });
      if (options.checkout) {
        window.location.href = '/checkout';
        return;
      }
      this.setStatus(this.settings.strings.addedToCart, 'success');
    } catch (error) {
      try {
        deferred.reject(error);
      } catch {
        /* settled */
      }
      this.setStatus(this.errorMessage(error), 'error');
    } finally {
      this.setBusy(false);
      this.syncActions();
    }
  }

  handleOpenLightbox() {
    const src = this.refs.portraitImage.currentSrc;
    if (!src) return;
    this.refs.lightboxImage.src = src;
    if (!this.refs.lightbox.open) this.refs.lightbox.showModal();
  }

  handleLightboxClick(event) {
    if (event.target === this.refs.lightbox) this.refs.lightbox.close();
  }

  handleLightboxClose() {
    this.refs.lightboxImage.removeAttribute('src');
  }

  resetDesign(options = {}) {
    this.#api?.abort();
    this.#uploadId = null;
    this.#uploadPromise = null;
    this.#uploading = false;
    this.#uploadFailed = false;
    this.#patterns = [];
    this.#confirmedDesign = null;
    this.#pendingPoll = null;
    this.#matted = false;
    this.setPortraitCutout(false);
    this.#selectedProduct = null;
    this.#selectedVariant = null;
    this.#optionSelections = {};
    this.refs.portraitResult.hidden = true;
    this.refs.portraitEmpty.hidden = true;
    this.refs.patternList.replaceChildren();
    this.refs.portraitImage.removeAttribute('src');
    this.updatePortraitPrintGuide();
    this.refs.detailDesignPreview.hidden = true;
    this.refs.detailDesignImage.removeAttribute('src');
    this.refs.detailPetName.hidden = true;
    this.refs.detailPetName.textContent = '';
    this.stopGenerationProgress();
    if (!options.keepFile) {
      this.#file = null;
      this.#originalFile = null;
      this.refs.fileInput.value = '';
      this.revokeObjectUrl();
      this.hideUploadPreview();
    } else {
      this.syncUploadOverlays();
    }
    this.refreshProductLists();
    this.syncActions();
  }

  setBusy(busy) {
    this.#busy = busy;
    this.syncActions();
    this.updateProductPreviewIssues(this.#products);
  }

  errorMessage(error) {
    if (error instanceof Error && error.name === 'AbortError') return '';
    if (error instanceof Error && error.message) return error.message;
    return this.settings.strings.errorGeneric;
  }

  uploadErrorMessage(error) {
    const message = this.errorMessage(error);
    if (!message || /^(load failed|failed to fetch|networkerror)$/i.test(message.trim())) {
      return this.settings.strings.errorUpload;
    }
    return message;
  }

  petName() {
    return cleanPetName(this.refs.petNameInput?.value || '');
  }

  /** @deprecated Use selectedBackgroundEffect() */
  wantsRemoveBackground() {
    const effect = this.selectedBackgroundEffect();
    return effect.mode === 'transparent' || effect.mode === 'color';
  }

  /**
   * @param {string} message
   * @param {number} value
   * @param {number} target
   */
  showGenerationProgress(message, value, target) {
    this.#progressMessage = message;
    this.#progressValue = Math.max(this.#progressValue, Math.min(100, value));
    this.#progressTarget = Math.max(this.#progressTarget, Math.min(100, target));
    this.renderGenerationProgress(message);
    this.updatePortraitPrintGuide();

    if (this.#progressTimer) return;
    this.#progressTimer = window.setInterval(() => {
      if (this.#progressValue >= this.#progressTarget) return;
      const distance = this.#progressTarget - this.#progressValue;
      this.#progressValue = Math.min(this.#progressTarget, this.#progressValue + Math.max(0.4, distance * 0.08));
      this.renderGenerationProgress(this.#progressMessage);
    }, 700);
  }

  /** @param {string} message */
  renderGenerationProgress(message) {
    const percent = Math.round(this.#progressValue);
    this.refs.portraitResult?.classList.add('is-generating');
    this.refs.portraitHint.textContent = message;
    this.refs.portraitHint.classList.remove('is-error');
    this.refs.portraitProgress.hidden = false;
    this.refs.portraitProgress.innerHTML = `
      <div class="create-portrait__generating" role="status" aria-live="polite">
        <span class="create-portrait__generating-spinner" aria-hidden="true"></span>
        <span class="create-portrait__generating-copy">
          <span>${message}</span>
          <strong>${percent}%</strong>
        </span>
        <span class="create-portrait__progress-bar" aria-hidden="true">
          <span style="width: ${percent}%"></span>
        </span>
      </div>
    `;
  }

  finishGenerationProgress() {
    this.#progressValue = 100;
    this.#progressTarget = 100;
    this.refs.portraitHint.textContent = this.settings.strings.confirmed;
    this.renderGenerationProgress(this.settings.strings.confirmed);
  }

  stopGenerationProgress() {
    if (this.#progressTimer) {
      window.clearInterval(this.#progressTimer);
      this.#progressTimer = 0;
    }
    this.#progressValue = 0;
    this.#progressTarget = 0;
    this.#progressMessage = '';
  }

  /**
   * @param {string} [message]
   * @param {'error'|'success'|''} [tone]
   */
  hideGenerationProgress(message = this.settings.strings.confirmed, tone = '') {
    this.stopGenerationProgress();
    this.refs.portraitResult?.classList.remove('is-generating');
    this.refs.portraitProgress.hidden = true;
    this.refs.portraitProgress.replaceChildren();
    this.refs.portraitHint.textContent = message;
    this.refs.portraitHint.classList.toggle('is-error', tone === 'error');
    this.updatePortraitPrintGuide();
  }

  startUpload() {
    if (!this.#file || !this.#api || this.#uploading) return;

    this.#uploadFailed = false;
    this.#uploading = true;
    this.#uploadId = null;
    this.refs.uploadSuccess.hidden = true;
    this.syncUploadOverlays();
    this.syncActions();
    this.setStatus(this.settings.strings.uploading);

    const uploadPromise = this.uploadFileWithFallback(this.#file)
      .then((result) => {
        if (this.#uploadPromise !== uploadPromise) return result;
        this.#uploadId = result.uploadId;
        this.#uploadFailed = false;
        if (result.sourceUrl?.startsWith('http')) this.refs.sourceImage.src = result.sourceUrl;
        this.refs.uploadSuccess.hidden = false;
        this.setStatus(this.settings.strings.uploadSuccess, 'success');
        return result;
      })
      .catch((error) => {
        if (this.#uploadPromise !== uploadPromise) return null;
        this.#uploadId = null;
        this.#uploadPromise = null;
        if (error instanceof Error && error.name === 'AbortError') {
          this.#uploadFailed = false;
          return null;
        }
        this.#uploadFailed = true;
        this.refs.uploadSuccess.hidden = true;
        this.setStatus(this.uploadErrorMessage(error), 'error');
        return null;
      })
      .finally(() => {
        if (this.#uploadPromise !== uploadPromise && this.#uploadPromise !== null) return;
        this.#uploading = false;
        this.syncUploadOverlays();
        this.syncActions();
      });
    this.#uploadPromise = uploadPromise;
  }

  handleRetryUpload(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!this.#file || this.#uploading || this.#busy) return;
    this.startUpload();
  }

  /**
   * @returns {Promise<{ uploadId: string, sourceUrl?: string }>}
   */
  async ensureUploaded() {
    if (this.#uploadId) return { uploadId: this.#uploadId };
    if (this.#uploadPromise) {
      const result = await this.#uploadPromise;
      if (result) return result;
    }
    if (!this.#file) throw new Error(this.settings.strings.errorUpload);

    const result = await this.uploadFileWithFallback(this.#file);
    this.#uploadId = result.uploadId;
    return result;
  }

  /**
   * @param {File | null} file
   * @returns {Promise<{ uploadId: string, sourceUrl?: string }>}
   */
  async uploadFileWithFallback(file) {
    if (!this.#api || !file) throw new Error(this.settings.strings.errorUpload);

    try {
      return await this.#api.upload(file);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') throw error;
      if (!this.#originalFile || this.#originalFile === file) throw error;

      if (this.dataset.debug === 'true') {
        console.info('[create-portrait] compressed upload failed; retrying original image');
      }
      const result = await this.#api.upload(this.#originalFile);
      this.#file = this.#originalFile;
      return result;
    }
  }

  revokeObjectUrl() {
    if (this.#objectUrl) {
      URL.revokeObjectURL(this.#objectUrl);
      this.#objectUrl = null;
    }
  }
}

if (!customElements.get('create-portrait-component')) {
  customElements.define('create-portrait-component', CreatePortraitComponent);
}

/**
 * @param {string} value
 * @returns {string}
 */
function cleanPetName(value) {
  return String(value || '').trim().replace(/\s+/g, ' ').slice(0, 40);
}

/**
 * Compresses browser-decodable image uploads before they are sent to the POD API.
 *
 * @param {File} file
 * @returns {Promise<{ file: File, compressed: boolean, originalSize: number, compressedSize: number }>}
 */
async function compressImageFile(file) {
  const originalSize = file.size || 0;
  if (!file.type.startsWith('image/')) {
    return { file, compressed: false, originalSize, compressedSize: originalSize };
  }

  try {
    const image = await decodeUploadImage(file);
    const scale = Math.min(1, IMAGE_COMPRESSION_MAX_DIMENSION / Math.max(image.width, image.height));
    const width = Math.max(1, Math.round(image.width * scale));
    const height = Math.max(1, Math.round(image.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('Canvas unavailable');

    context.fillStyle = '#fff';
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    closeDecodedUploadImage(image);

    let quality = IMAGE_COMPRESSION_QUALITY;
    let blob = await uploadCanvasToBlob(canvas, quality);
    while (blob.size > IMAGE_COMPRESSION_MAX_BYTES && quality > IMAGE_COMPRESSION_MIN_QUALITY) {
      quality = Math.max(IMAGE_COMPRESSION_MIN_QUALITY, quality - 0.08);
      blob = await uploadCanvasToBlob(canvas, quality);
    }

    if (blob.size >= originalSize && scale === 1) {
      return { file, compressed: false, originalSize, compressedSize: originalSize };
    }

    const compressedFile = new File([blob], compressedUploadName(file.name), {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
    return {
      file: compressedFile,
      compressed: compressedFile.size < originalSize || scale < 1,
      originalSize,
      compressedSize: compressedFile.size,
    };
  } catch {
    return { file, compressed: false, originalSize, compressedSize: originalSize };
  }
}

/**
 * @param {File} file
 * @returns {Promise<ImageBitmap | HTMLImageElement>}
 */
async function decodeUploadImage(file) {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // Fall back to HTMLImageElement decoding below.
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    await image.decode();
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * @param {ImageBitmap | HTMLImageElement} image
 */
function closeDecodedUploadImage(image) {
  if ('close' in image && typeof image.close === 'function') image.close();
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {number} quality
 * @returns {Promise<Blob>}
 */
function uploadCanvasToBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Image compression failed'));
      },
      'image/jpeg',
      quality
    );
  });
}

/**
 * @param {string} name
 */
function compressedUploadName(name) {
  const base = String(name || 'upload').replace(/\.[^.]+$/, '') || 'upload';
  return `${base}.jpg`;
}

/**
 * @param {number} boxW
 * @param {number} boxH
 * @param {number} imgW
 * @param {number} imgH
 */
function containRect(boxW, boxH, imgW, imgH) {
  const scale = Math.min(boxW / imgW, boxH / imgH);
  const w = imgW * scale;
  const h = imgH * scale;
  return {
    x: (boxW - w) / 2,
    y: (boxH - h) / 2,
    w,
    h,
  };
}

/**
 * @param {FormData} formData
 * @returns {Record<string, string | { name: string, size: number, type: string }>}
 */
function lineItemPropertiesFromFormData(formData) {
  /** @type {Record<string, string | { name: string, size: number, type: string }>} */
  const properties = {};
  for (const [key, value] of formData.entries()) {
    const match = /^properties\[(.+)\]$/.exec(key);
    if (!match) continue;
    properties[match[1]] =
      value instanceof File ? { name: value.name, size: value.size, type: value.type } : String(value);
  }
  return properties;
}
